import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import "./App.css";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const getElementImageSource = (element) => {
  if (element.image) {
    // If image path is set, it might be 'images/path.png' or 'http...'
    const base = element.image.startsWith("http")
      ? element.image
      : `/${element.image}`;
    // Add a small versioning based on the filename or current time if it's likely being edited
    return base;
  }
  // Multiple fallbacks
  const lookupId = element.catalogId || element.id;
  return [
    `/images/${lookupId}.png`,
    `/images/${lookupId}.jpg`,
    `/images/${lookupId}.svg`,
  ];
};

const SidebarElementImage = ({ element, className, height = "40px" }) => {
  const sources = Array.isArray(getElementImageSource(element))
    ? getElementImageSource(element)
    : [getElementImageSource(element)];
  const [sourceIndex, setSourceIndex] = useState(0);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleImageError = () => {
    if (sourceIndex < sources.length - 1) {
      setSourceIndex((prev) => prev + 1);
    } else {
      setError(true);
      setLoading(false);
    }
  };

  const handleImageLoad = () => {
    setLoading(false);
  };

  if (error) {
    // According to user request: show nothing if image doesn't exist
    return null;
  }

  return (
    <div className={`${className} ${loading ? "loading" : ""}`}>
      <img
        src={`${sources[sourceIndex]}${element.image ? "?v=" + element.image.split("_").pop().split(".")[0] : ""}`}
        alt={element.name}
        style={{
          height: height,
          width: "auto",
          borderRadius: "4px",
          display: loading ? "none" : "block",
        }}
        onError={handleImageError}
        onLoad={handleImageLoad}
        draggable="false"
      />
      {loading && (
        <div
          className="image-loader-spinner"
          style={{ height: height, width: height }}
        ></div>
      )}
    </div>
  );
};

const GRID_SIZE = 50;
const HORIZONTAL_OPTICAL_SCALE = 1.3;
const snapToGrid = (value) => Math.round(value / GRID_SIZE) * GRID_SIZE;

const getBausteinSpecs = (baustein) => {
  const catalogId = baustein.catalogId || baustein.id;
  const idPart = catalogId.split("-")[0];
  const F = parseInt(idPart[2]) || 1;
  const nUnits = parseInt(idPart[3]) || 1;

  // For 'normale' category, we split the baustein into separate 12-slot parts if it consists of multiple units.
  // We use baustein.size and baustein.ibreite to determine the number of compartments (S).
  if (baustein.category === "normale") {
    const size = baustein.size || nUnits;
    const ibreite = baustein.ibreite || 1;
    const S = Math.max(1, Math.floor(size / ibreite));
    // Each compartment has slots based on ibreite (usually 12 for ibreite=1)
    const maxSlots = 12 * ibreite + 2 * (ibreite - 1);
    return { F, S, maxSlots };
  }

  // Original logic for 'klemmen' and other categories (keep them unified)
  // Total slots: 12 per unit + 2 bonus slots for each boundary between units.
  const totalSlots = 12 * nUnits + 2 * (nUnits - 1);
  return { F, S: 1, maxSlots: totalSlots };
};

function App() {
  const [bausteine, setBausteine] = useState([]);
  const [elements, setElements] = useState([]);
  const canvasRef = useRef(null);
  const imageCache = useRef({});
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMouse, setLastMouse] = useState({ x: 0, y: 0 });
  const [placedBausteine, setPlacedBausteine] = useState([]);
  const [draggedBaustein, setDraggedBaustein] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [mode, setMode] = useState("s");
  const [bausteinElements, setBausteinElements] = useState({});
  const [selectedBaustein, setSelectedBaustein] = useState(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const forceUpdate = useCallback(
    () => setUpdateTrigger((prev) => prev + 1),
    [],
  );
  const [draggedElement, setDraggedElement] = useState(null);
  const [draggedFromSidebar, setDraggedFromSidebar] = useState(false);
  const [groupName, setGroupName] = useState("Verteilerschrank");
  const [draggedPlacedElementInfo, setDraggedPlacedElementInfo] =
    useState(null);
  const [draggedElementPosition, setDraggedElementPosition] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [groupWidth, setGroupWidth] = useState(2);
  const [groupHeight, setGroupHeight] = useState(2);
  const [alerts, setAlerts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [elementCategories, setElementCategories] = useState([]);
  const [selectedBausteinCategory, setSelectedBausteinCategory] =
    useState("all");
  const [confirmation, setConfirmation] = useState(null);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [nextBausteinSerial, setNextBausteinSerial] = useState(1);
  const [nextElementSerial, setNextElementSerial] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [isLoaded, setIsLoaded] = useState(false);
  const [settings, setSettings] = useState({ showprices: true });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    street: "",
    zip: "",
    city: "",
    message: "",
  });

  useEffect(() => {
    fetch(`/catalog.json?v=${Date.now()}`)
      .then((res) => res.json())
      .then((data) => {
        setBausteine((data.bausteine || []).filter((b) => b.aktiv !== false));
        setElements((data.elements || []).filter((el) => el.aktiv !== false));
        if (data.settings) {
          setSettings(data.settings);
        }
        if (data.categories && data.categories.elements) {
          setElementCategories(data.categories.elements);
        }
      })
      .catch((err) => console.error("Error loading catalog.json:", err));
  }, []);

  useEffect(() => {
    const savedConfig = localStorage.getItem("kf3_configuration");
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        setPlacedBausteine(config.placedBausteine || []);
        setBausteinElements(config.bausteinElements || {});
        setGroupName(config.groupName || "Verteilerschrank");
        setGroupWidth(config.groupWidth || 2);
        setGroupHeight(config.groupHeight || 2);
        setNextBausteinSerial(config.nextBausteinSerial || 1);
        setNextElementSerial(config.nextElementSerial || 1);
      } catch (err) {
        console.error("Error parsing saved configuration:", err);
      }
    }
    setIsLoaded(true);
  }, []);

  const getCachedImage = useCallback(
    (src, version = "") => {
      if (!src) return null;

      // Support array of sources for fallbacks
      const sources = Array.isArray(src) ? src : [src];
      const primarySrc = sources[0];

      if (imageCache.current[primarySrc]) {
        const entry = imageCache.current[primarySrc];
        if (entry === "error") {
          // If the whole stack failed
          return "error";
        }
        if (entry instanceof HTMLImageElement) return entry;
        // If it's a number, it's the index of the source currently being tried
        if (typeof entry === "number") {
          return "loading";
        }
        return entry; // 'loading'
      }

      const trySource = (index) => {
        const currentSrc = sources[index];
        imageCache.current[primarySrc] = index; // Store which index we are trying

        const img = new Image();
        img.onload = () => {
          imageCache.current[primarySrc] = img;
          forceUpdate();
        };
        img.onerror = () => {
          if (index < sources.length - 1) {
            trySource(index + 1);
          } else {
            imageCache.current[primarySrc] = "error";
            forceUpdate();
          }
        };
        img.src = `${currentSrc}${version ? "?v=" + version : ""}`;
      };

      trySource(0);
      return "loading";
    },
    [forceUpdate],
  );

  const uniqueCategoriesFlattened = useMemo(() => {
    const flatten = (cats, prefix = "", level = 0) => {
      let results = [];
      if (!cats) return results;
      cats.forEach((c) => {
        const fullPath = prefix ? `${prefix} > ${c.name}` : c.name;
        results.push({ name: c.name, path: fullPath, level: level });
        if (c.subs && c.subs.length > 0) {
          results = results.concat(flatten(c.subs, fullPath, level + 1));
        }
      });
      return results;
    };
    return [
      { name: "Alle Kategorien", path: "all", level: 0 },
      ...flatten(elementCategories),
    ];
  }, [elementCategories]);
  const uniqueBausteinCategories = [
    "all",
    ...new Set(bausteine.map((b) => b.category)),
  ];

  const totalPrice =
    placedBausteine.reduce((sum, cab) => sum + (cab.price || 0), 0) +
    Object.values(bausteinElements)
      .flat()
      .reduce((sum, el) => sum + (el.price || 0), 0);

  const getSummary = () => {
    const summary = {
      bausteine: {},
      elements: {},
    };

    placedBausteine.forEach((cab) => {
      // Use the unique cab.id to prevent grouping
      summary.bausteine[cab.id] = {
        ...cab,
        count: 1,
        total: cab.price || 0,
      };
    });

    Object.entries(bausteinElements).forEach(([cabId, elements]) => {
      elements.forEach((el) => {
        // el.id is now a unique serial ID like 'E0001'
        summary.elements[el.id] = {
          ...el,
          count: 1,
          total: el.price || 0,
        };
      });
    });

    return summary;
  };

  const summaryData = getSummary();

  const handleSendRequest = () => {
    if (!contactForm.name || !contactForm.email) {
      triggerAlert("Bitte geben Sie Name und E-Mail an.", "error");
      return;
    }

    if (placedBausteine.length === 0) {
      triggerAlert(
        "Ihr Warenkorb ist leer. Bitte platzieren Sie zuerst Bausteine.",
        "error",
      );
      return;
    }

    triggerAlert("PDF wird generiert...", "info");

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();

      // Header
      doc.setFontSize(22);
      doc.setTextColor(251, 191, 11); // #fbbf0b
      doc.text("Anfrage: Verteilerschrank", 14, 20);

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Datum: ${new Date().toLocaleDateString("de-DE")}`, 14, 30);

      // Customer Info
      doc.setFontSize(14);
      doc.text("Kundeninformationen", 14, 45);
      doc.setFontSize(10);
      doc.text(`Name: ${contactForm.name}`, 14, 52);
      doc.text(`E-Mail: ${contactForm.email}`, 14, 58);

      let currentY = 64;
      if (contactForm.phone) {
        doc.text(`Telefon: ${contactForm.phone}`, 14, currentY);
        currentY += 6;
      }

      const addressParts = [];
      if (contactForm.street) addressParts.push(contactForm.street);
      const zipCity = [contactForm.zip, contactForm.city].filter(Boolean).join(" ");
      if (zipCity) addressParts.push(zipCity);

      if (addressParts.length > 0) {
        doc.text(`Angebotsadresse: ${addressParts.join(", ")}`, 14, currentY);
        currentY += 6;
      }

      if (contactForm.message) {
        doc.text("Bemerkungen:", 14, currentY);
        const splitMessage = doc.splitTextToSize(
          contactForm.message,
          pageWidth - 28,
        );
        doc.text(splitMessage, 14, currentY + 6);
        currentY += 10 + splitMessage.length * 5;
      } else {
        currentY += 5;
      }

      // Bausteine grouping
      const bausteineRows = [];
      const groupedBausteine = placedBausteine.reduce((acc, b) => {
        const key = b.catalogId || b.id;
        if (!acc[key]) {
          acc[key] = { name: b.name, id: key, price: b.price || 0, count: 0 };
        }
        acc[key].count++;
        return acc;
      }, {});

      Object.values(groupedBausteine).forEach((b) => {
        if (settings.showprices) {
          bausteineRows.push([
            b.id,
            b.name,
            b.count,
            `${b.price.toFixed(2)} €`,
            `${(b.price * b.count).toFixed(2)} €`,
          ]);
        } else {
          bausteineRows.push([b.id, b.name, b.count, "", ""]);
        }
      });

      // Elements grouping
      const elementsRows = [];
      const allElements = Object.values(bausteinElements).flat();
      const groupedElements = allElements.reduce((acc, el) => {
        const key = el.catalogId || el.id;
        if (!acc[key]) {
          acc[key] = { name: el.name, id: key, price: el.price || 0, count: 0 };
        }
        acc[key].count++;
        return acc;
      }, {});

      Object.values(groupedElements).forEach((el) => {
        if (settings.showprices) {
          elementsRows.push([
            el.id,
            el.name,
            el.count,
            `${el.price.toFixed(2)} €`,
            `${(el.price * el.count).toFixed(2)} €`,
          ]);
        } else {
          elementsRows.push([el.id, el.name, el.count, "", ""]);
        }
      });

      if (bausteineRows.length > 0) {
        doc.setFontSize(14);
        doc.text("Bausteine", 14, currentY);
        doc.autoTable({
          startY: currentY + 5,
          head: [
            settings.showprices
              ? ["Art.Nr.", "Bezeichnung", "Menge", "Einzelpreis", "Gesamt"]
              : ["Art.Nr.", "Bezeichnung", "Menge", "", ""],
          ],
          body: bausteineRows,
          theme: "striped",
          headStyles: { fillColor: [251, 191, 11], textColor: [0, 0, 0] },
          margin: { left: 14, right: 14 },
        });
        currentY = doc.lastAutoTable.finalY + 15;
      }

      if (elementsRows.length > 0) {
        if (currentY > doc.internal.pageSize.getHeight() - 40) {
          doc.addPage();
          currentY = 20;
        }
        doc.setFontSize(14);
        doc.text("Elemente", 14, currentY);
        doc.autoTable({
          startY: currentY + 5,
          head: [
            settings.showprices
              ? ["Art.Nr.", "Bezeichnung", "Menge", "Einzelpreis", "Gesamt"]
              : ["Art.Nr.", "Bezeichnung", "Menge", "", ""],
          ],
          body: elementsRows,
          theme: "striped",
          headStyles: { fillColor: [251, 191, 11], textColor: [0, 0, 0] },
          margin: { left: 14, right: 14 },
        });
        currentY = doc.lastAutoTable.finalY + 15;
      }

      // Total
      if (currentY > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage();
        currentY = 20;
      }
      if (settings.showprices) {
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text(
          `Gesamtbetrag: ${totalPrice.toFixed(2)} €`,
          pageWidth - 14,
          currentY,
          { align: "right" },
        );
      }

      // Add extra page for visual assembly plan in LANDSCAPE (Querformat)
      doc.addPage("a4", "l");

      const landscapeWidth = doc.internal.pageSize.getWidth();
      const landscapeHeight = doc.internal.pageSize.getHeight();

      doc.setFontSize(18);
      doc.setTextColor(251, 191, 11); // #fbbf0b
      doc.text("Aufbauplan - Verteilerschrank", 14, 18);

      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(
        `Schrank: ${groupName || "Verteilerschrank"} | Abmessungen: ${groupWidth * 250} mm (Breite) x ${groupHeight * 150} mm (Höhe)`,
        14,
        25,
      );

      const diagramImageData = generateCabinetDiagramImage(
        placedBausteine,
        bausteinElements,
        groupWidth,
        groupHeight,
        groupName,
      );

      if (diagramImageData) {
        const maxImgWidth = landscapeWidth - 28; // 14mm margins
        const maxImgHeight = landscapeHeight - 38; // Margin for header and bottom

        const imgProps = doc.getImageProperties(diagramImageData);
        const imgAspect = imgProps.width / imgProps.height;

        let imgWidth = maxImgWidth;
        let imgHeight = maxImgWidth / imgAspect;

        if (imgHeight > maxImgHeight) {
          imgHeight = maxImgHeight;
          imgWidth = maxImgHeight * imgAspect;
        }

        const imgX = 14 + (maxImgWidth - imgWidth) / 2;
        const imgY = 28 + (maxImgHeight - imgHeight) / 2;
        doc.addImage(diagramImageData, "PNG", imgX, imgY, imgWidth, imgHeight);
      }

      // Add a separate PORTRAIT page for the Montage- & Bestückungsliste Table
      doc.addPage("a4", "p");

      doc.setFontSize(18);
      doc.setTextColor(251, 191, 11); // #fbbf0b
      doc.text("Montage- & Bestückungsliste", 14, 20);

      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(
        `Detaillierte Bauteil-Zuordnung für Schrank: ${groupName || "Verteilerschrank"}`,
        14,
        27,
      );

      const assemblyRows = [];

      placedBausteine.forEach((baustein) => {
        const layout = getBausteinLayout(baustein);
        const elements = bausteinElements[baustein.id] || [];

        elements.forEach((element) => {
          const compIndex = element.compIndex || 0;
          const row = Math.floor(compIndex / layout.S) + 1;
          const col = (compIndex % layout.S) + 1;
          const slot = element.slot !== undefined ? element.slot : 0;
          const pleSize = element.size || 1;

          assemblyRows.push([
            `${baustein.catalogId || baustein.id} (${baustein.id})`,
            `Reihe ${row}, Feld ${col}`,
            `Slot ${slot}`,
            element.id || element.catalogId || "-",
            element.name || "Unbekanntes Element",
            `${pleSize} PLE`,
          ]);
        });
      });

      if (assemblyRows.length > 0) {
        doc.autoTable({
          startY: 32,
          head: [
            [
              "Modul (Baustein)",
              "Position",
              "Start-Slot",
              "Art.Nr. / ID",
              "Bezeichnung",
              "Größe",
            ],
          ],
          body: assemblyRows,
          theme: "striped",
          headStyles: { fillColor: [251, 191, 11], textColor: [0, 0, 0] },
          margin: { left: 14, right: 14 },
          styles: { fontSize: 8 },
        });
      } else {
        doc.setFontSize(10);
        doc.setTextColor(120, 120, 120);
        doc.text(
          "Keine Baustein-Elemente auf den Hutschienen platziert.",
          14,
          37,
        );
      }

      const pdfBlob = doc.output('blob');

      // In local dev environment, open the generated PDF in a new tab
      const isLocalDev =
        process.env.NODE_ENV === "development" ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      if (isLocalDev) {
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, "_blank");
      }

      const filename = `Anfrage_Verteilerschrank_${contactForm.name.replace(/\s+/g, "_")}.pdf`;

      const fullAddress = [
        contactForm.street,
        [contactForm.zip, contactForm.city].filter(Boolean).join(" "),
      ]
        .filter(Boolean)
        .join(", ");

      const formData = new FormData();
      formData.append('pdf', pdfBlob, filename);
      formData.append('name', contactForm.name);
      formData.append('email', contactForm.email);
      formData.append('phone', contactForm.phone || '');
      formData.append('street', contactForm.street || '');
      formData.append('zip', contactForm.zip || '');
      formData.append('city', contactForm.city || '');
      formData.append('address', fullAddress);
      formData.append('message', contactForm.message || '');

      triggerAlert("Anfrage wird gesendet...", "info");

      fetch('/send_order.php', {
        method: 'POST',
        body: formData,
      })
        .then(async (response) => {
          const data = await response.json();
          if (data.success) {
            triggerAlert("Ihre Anfrage wurde erfolgreich gesendet!", "success");
          } else {
            triggerAlert(
              "Fehler beim Senden: " + (data.error || "Unbekannter Fehler"),
              "error",
            );
          }
        })
        .catch((err) => {
          console.error("Fetch error:", err);
          triggerAlert(
            "Fehler beim Senden der Anfrage. Bitte versuchen Sie es erneut.",
            "error",
          );
        });
    } catch (error) {
      console.error("PDF generation error:", error);
      triggerAlert("Fehler bei der PDF-Erstellung.", "error");
    }
  };

  const triggerAlert = (message, type = "warning") => {
    const id = Date.now();
    setAlerts((prev) => [...prev, { id, message, type }]);
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    }, 5000);
  };

  const canvasContainerWidth = windowSize.width - 300;
  const canvasContainerHeight = windowSize.height;

  // getBausteinSpecs is already defined globally, no need to redefine here.

  const getBausteinLayout = useCallback((baustein) => {
    const { F, S, maxSlots } = getBausteinSpecs(baustein);
    const { width, height, category } = baustein;
    const isKlemmen = category === "klemmen";
    const isNormal = category === "normale";

    const railHeight = 25;
    const verticalMargin = 20;
    const compMargin = isKlemmen ? 0 : 15;
    const compPadding = 2; // Fixed to match normale
    const compH = 58; // Fixed to match normale

    const compWidth = (width - (S + 1) * compMargin) / S;
    const compHeightAvailable = height - 2 * railHeight - 2 * verticalMargin;
    const totalCompsHeight = F * compH;
    const spaceAvailable = compHeightAvailable - totalCompsHeight;
    const spaceBetween = F > 1 ? spaceAvailable / (F - 1) : spaceAvailable / 2;

    const compColor = isKlemmen ? "#e3e3e3" : "white";

    // For elements
    // Adjusted for 'normale' category to match reference (taller elements)
    const elementYOffset = isNormal ? 25 : 15;
    const elementHeightAdd = isNormal ? 50 : 30;

    // For placeholders/ghosts
    const placeholderYOffset = 22;
    const placeholderHeightAdd = 52;

    return {
      F,
      S,
      maxSlots,
      railHeight,
      verticalMargin,
      compMargin,
      compPadding,
      compH,
      compWidth,
      compColor,
      spaceBetween,
      elementYOffset,
      elementHeightAdd,
      placeholderYOffset,
      placeholderHeightAdd,
      compYBase: baustein.y + railHeight + verticalMargin,
    };
  }, []);

  const getElementColor = useCallback((size) => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
      "#F8C471",
      "#82E0AA",
    ];
    // Use Math.floor and multiply by 4 to give 0.25 sizes their own color index
    const index = Math.abs(Math.floor(size * 4 - 1)) % colors.length;
    return colors[index];
  }, []);

  const checkCollision = (
    newBaustein,
    excludeIndex = -1,
    bausteineArray = placedBausteine,
  ) => {
    return bausteineArray.some((baustein, index) => {
      if (index === excludeIndex) return false;
      return !(
        newBaustein.x >= baustein.x + baustein.width ||
        newBaustein.x + newBaustein.width <= baustein.x ||
        newBaustein.y >= baustein.y + baustein.height ||
        newBaustein.y + newBaustein.height <= baustein.y
      );
    });
  };

  const reorganizeElements = (
    stationaryElements,
    movingElement = null,
    maxSlots = 12,
  ) => {
    // Create copies and strip any existing isMoving flag to avoid state contamination
    let stationary = stationaryElements.map((el) => {
      const { isMoving, ...rest } = el;
      return rest;
    });

    if (!movingElement) return stationary.sort((a, b) => a.slot - b.slot);

    let moving = { ...movingElement };
    moving.isMoving = true;

    const targetSlot = moving.slot;
    const movingMid = targetSlot + moving.size / 2;

    // Split stationary elements based on their center relative to the moving element's center
    let leftSide = stationary.filter((el) => el.slot + el.size / 2 < movingMid);
    let rightSide = stationary.filter(
      (el) => el.slot + el.size / 2 >= movingMid,
    );

    leftSide.sort((a, b) => a.slot - b.slot);
    rightSide.sort((a, b) => a.slot - b.slot);

    // Push leftSide elements further left if they overlap with moving element
    let rightBoundary = targetSlot;
    for (let i = leftSide.length - 1; i >= 0; i--) {
      if (leftSide[i].slot + leftSide[i].size > rightBoundary) {
        leftSide[i].slot = rightBoundary - leftSide[i].size;
      }
      rightBoundary = leftSide[i].slot;
    }

    // Push rightSide elements further right if they overlap with moving element
    let leftBoundary = targetSlot + moving.size;
    for (let i = 0; i < rightSide.length; i++) {
      if (rightSide[i].slot < leftBoundary) {
        rightSide[i].slot = leftBoundary;
      }
      leftBoundary = rightSide[i].slot + rightSide[i].size;
    }

    // If we pushed past the left edge (0), shift the whole group to the right
    let leftmost = leftSide.length > 0 ? leftSide[0] : moving;
    if (leftmost.slot < 0) {
      let shift = -leftmost.slot;
      leftSide.forEach((el) => (el.slot += shift));
      moving.slot += shift;
      rightSide.forEach((el) => (el.slot += shift));

      // After shifting right, we might have pushed the rightmost element past the right edge again.
      // To fix this without overlapping, we pack elements tightly from the right edge.
      let rightmost_idx = rightSide.length > 0 ? rightSide.length - 1 : -1;
      let currentRightmost =
        rightmost_idx >= 0 ? rightSide[rightmost_idx] : moving;
      if (currentRightmost.slot + currentRightmost.size > maxSlots) {
        let currentSlot = maxSlots;
        for (let i = rightSide.length - 1; i >= 0; i--) {
          let el = rightSide[i];
          if (el.slot + el.size > currentSlot) el.slot = currentSlot - el.size;
          currentSlot = el.slot;
        }
        if (moving.slot + moving.size > currentSlot)
          moving.slot = currentSlot - moving.size;
        currentSlot = moving.slot;
        for (let i = leftSide.length - 1; i >= 0; i--) {
          let el = leftSide[i];
          if (el.slot + el.size > currentSlot) el.slot = currentSlot - el.size;
          currentSlot = el.slot;
        }
      }
    }

    // If we pushed past the right edge (maxSlots), shift the whole group to the left
    let rightmost =
      rightSide.length > 0 ? rightSide[rightSide.length - 1] : moving;
    if (rightmost.slot + rightmost.size > maxSlots) {
      let shift = rightmost.slot + rightmost.size - maxSlots;
      leftSide.forEach((el) => (el.slot -= shift));
      moving.slot -= shift;
      rightSide.forEach((el) => (el.slot -= shift));

      // After shifting left, we might have pushed the leftmost element past the left edge again.
      // To fix this without overlapping, we pack elements tightly from the left edge.
      leftmost = leftSide.length > 0 ? leftSide[0] : moving;
      if (leftmost.slot < 0) {
        let currentSlot = 0;
        leftSide.forEach((el) => {
          if (el.slot < currentSlot) el.slot = currentSlot;
          currentSlot = el.slot + el.size;
        });
        if (moving.slot < currentSlot) moving.slot = currentSlot;
        currentSlot = moving.slot + moving.size;
        rightSide.forEach((el) => {
          if (el.slot < currentSlot) el.slot = currentSlot;
          currentSlot = el.slot + el.size;
        });
      }
    }

    return [...leftSide, moving, ...rightSide];
  };

  const generateCabinetDiagramImage = useCallback(
    (placedBausteine, bausteinElements, groupWidth, groupHeight, groupName) => {
      try {
        const offCanvas = document.createElement("canvas");
        const groupVisualWidth = groupWidth * 250;
        const groupVisualHeight = groupHeight * 150;
        const borderWidth = 32;
        const margin = 100; // Extra space around cabinet for dimensions & labels

        const totalLogicalWidth = groupVisualWidth * HORIZONTAL_OPTICAL_SCALE + margin * 2;
        const totalLogicalHeight = groupVisualHeight + margin * 2;

        const scale = 2; // High-resolution multiplier
        offCanvas.width = totalLogicalWidth * scale;
        offCanvas.height = totalLogicalHeight * scale;

        const ctx = offCanvas.getContext("2d");
        ctx.scale(scale, scale);

        // White background for print
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, totalLogicalWidth, totalLogicalHeight);

        ctx.save();
        ctx.translate(margin, margin);

        // 1. Draw outer cabinet enclosure border
        ctx.strokeStyle = "#888888";
        ctx.lineWidth = borderWidth;
        const offset = borderWidth / 2;
        ctx.strokeRect(
          -offset * HORIZONTAL_OPTICAL_SCALE,
          -offset,
          groupVisualWidth * HORIZONTAL_OPTICAL_SCALE + 2 * offset * HORIZONTAL_OPTICAL_SCALE,
          groupVisualHeight + 2 * offset,
        );

        // 2. Draw fine grid unit lines (250mm x 150mm)
        ctx.save();
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = "rgba(0, 0, 0, 0.2)";
        ctx.lineWidth = 1;

        for (let i = 1; i < groupWidth; i++) {
          const x = i * 250 * HORIZONTAL_OPTICAL_SCALE;
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, groupVisualHeight);
          ctx.stroke();
        }

        for (let i = 1; i < groupHeight; i++) {
          const y = i * 150;
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(groupVisualWidth * HORIZONTAL_OPTICAL_SCALE, y);
          ctx.stroke();
        }
        ctx.restore();

        // 3. Draw Placed Bausteine with HORIZONTAL_OPTICAL_SCALE transform
        ctx.save();
        ctx.scale(HORIZONTAL_OPTICAL_SCALE, 1);

        placedBausteine.forEach((baustein) => {
          const { x, y, width, height, category } = baustein;
          const isNormal = category === "normale";

          if (!isNormal) {
            ctx.fillStyle = "#f8f8f8";
            ctx.fillRect(x, y, width, height);
            ctx.strokeStyle = "#333";
            ctx.lineWidth = 2;
            ctx.strokeRect(x, y, width, height);
          }

          const layout = getBausteinLayout(baustein);
          const {
            F,
            S,
            maxSlots,
            railHeight,
            compMargin,
            compPadding,
            compH,
            compWidth,
            compColor,
            spaceBetween,
            compYBase,
          } = layout;

          // Top rail
          ctx.fillStyle = "white";
          ctx.fillRect(x, y, width, railHeight);
          ctx.strokeStyle = "black";
          ctx.lineWidth = 1;
          ctx.strokeRect(x, y, width, railHeight);

          // Corner crosshairs
          const drawCornerPDF = (cx, cy) => {
            ctx.save();
            ctx.translate(cx, cy);
            ctx.scale(1 / HORIZONTAL_OPTICAL_SCALE, 1);
            const radius = 8.5;
            ctx.beginPath();
            ctx.arc(0, 0, radius, 0, 2 * Math.PI);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(-radius - 2, 0);
            ctx.lineTo(radius + 2, 0);
            ctx.moveTo(0, -radius - 2);
            ctx.lineTo(0, radius + 2);
            ctx.stroke();
            ctx.restore();
          };

          drawCornerPDF(x + 12.5, y + 12.5);
          drawCornerPDF(x + width - 12.5, y + 12.5);

          // Compartments
          for (let r = 0; r < F; r++) {
            for (let c = 0; c < S; c++) {
              const compXPos = x + compMargin + c * (compWidth + compMargin);
              const compYPos =
                compYBase +
                (F === 1 ? spaceBetween / 2 : 0) +
                r * (compH + spaceBetween);

              ctx.fillStyle = compColor;
              ctx.fillRect(compXPos, compYPos, compWidth, compH);
              ctx.strokeStyle = "#555";
              ctx.lineWidth = 1;
              ctx.strokeRect(compXPos, compYPos, compWidth, compH);
            }
          }

          // Bottom rail
          const bottomY = y + height - railHeight;
          ctx.fillStyle = "white";
          ctx.fillRect(x, bottomY, width, railHeight);
          ctx.strokeStyle = "black";
          ctx.lineWidth = 1;
          ctx.strokeRect(x, bottomY, width, railHeight);

          drawCornerPDF(x + 12.5, bottomY + 12.5);
          drawCornerPDF(x + width - 12.5, bottomY + 12.5);

          // Side rails
          ctx.beginPath();
          ctx.moveTo(x, y + railHeight);
          ctx.lineTo(x, bottomY);
          ctx.moveTo(x + width, y + railHeight);
          ctx.lineTo(x + width, bottomY);
          ctx.stroke();

          // Baustein Label (Catalog ID + Instance ID)
          ctx.fillStyle = "#111";
          ctx.textAlign = "center";
          ctx.save();
          ctx.translate(x + width / 2, y + 16);
          ctx.scale(1 / HORIZONTAL_OPTICAL_SCALE, 1);
          ctx.font = "bold 12px Arial";
          ctx.fillText(`${baustein.catalogId || baustein.id} - ${baustein.id}`, 0, 0);
          ctx.restore();

          // Draw elements inside Baustein
          const elements = bausteinElements[baustein.id] || [];

          for (let r = 0; r < F; r++) {
            for (let c = 0; c < S; c++) {
              const compIndex = r * S + c;
              const compX = x + compMargin + c * (compWidth + compMargin);
              const compYPos =
                compYBase +
                (F === 1 ? spaceBetween / 2 : 0) +
                r * (compH + spaceBetween);

              const elementsInComp = elements.filter(
                (el) => (el.compIndex || 0) === compIndex,
              );

              elementsInComp.forEach((element, i) => {
                if (element && !element.isMoving) {
                  const slot = element.slot !== undefined ? element.slot : i;
                  const elemX =
                    compX +
                    compPadding +
                    (slot / maxSlots) * (compWidth - 2 * compPadding);
                  let elemHeight = element.klemmen ? compH + 30 : compH;
                  let elemWidth =
                    (element.size / maxSlots) * (compWidth - 2 * compPadding);
                  const elemY = compYPos + (compH - elemHeight) / 2;

                  const sources = getElementImageSource(element);
                  const version = element.image
                    ? element.image.split("_").pop().split(".")[0]
                    : "";
                  const cachedImg = getCachedImage(sources, version);

                  if (cachedImg instanceof HTMLImageElement) {
                    ctx.drawImage(cachedImg, elemX, elemY, elemWidth, elemHeight);
                  } else {
                    // Fallback block if image isn't loaded
                    ctx.fillStyle = getElementColor(element.size);
                    ctx.fillRect(elemX, elemY, elemWidth, elemHeight);
                  }

                  // Element Border
                  ctx.strokeStyle = "rgba(0, 0, 0, 0.4)";
                  ctx.lineWidth = 1;
                  ctx.strokeRect(elemX + 0.5, elemY + 0.5, elemWidth - 1, elemHeight - 1);

                  // Element identification badge (ID / Name)
                  ctx.save();
                  ctx.translate(elemX + elemWidth / 2, elemY + elemHeight / 2);
                  ctx.scale(1 / HORIZONTAL_OPTICAL_SCALE, 1);
                  ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
                  ctx.fillRect(-elemWidth * HORIZONTAL_OPTICAL_SCALE / 2 + 2, -7, elemWidth * HORIZONTAL_OPTICAL_SCALE - 4, 14);
                  ctx.fillStyle = "#000000";
                  ctx.font = "bold 9px Arial";
                  ctx.textAlign = "center";
                  ctx.textBaseline = "middle";
                  const displayLabel = `${element.id || element.catalogId}`;
                  ctx.fillText(displayLabel, 0, 0);
                  ctx.restore();
                }
              });
            }
          }
        });

        ctx.restore(); // Restore optical scale transform

        // 4. Draw outer dimensions (width & height in mm)
        const dimOffset = 45;
        const mmWidth = groupWidth * 250;
        const mmHeight = groupHeight * 150;
        const scaledGroupWidth = groupVisualWidth * HORIZONTAL_OPTICAL_SCALE;

        // Top Width Dimension Line
        const topDimY = -dimOffset;
        ctx.strokeStyle = "#333333";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(0, topDimY);
        ctx.lineTo(scaledGroupWidth, topDimY);
        ctx.stroke();

        // Ticks
        ctx.beginPath();
        ctx.moveTo(0, topDimY - 8);
        ctx.lineTo(0, topDimY + 8);
        ctx.moveTo(scaledGroupWidth, topDimY - 8);
        ctx.lineTo(scaledGroupWidth, topDimY + 8);
        ctx.stroke();

        // Width Label Box
        ctx.fillStyle = "white";
        ctx.font = "bold 13px Arial";
        ctx.textAlign = "center";
        const widthText = `${mmWidth} mm`;
        const wTextWidth = ctx.measureText(widthText).width + 16;
        ctx.fillRect(scaledGroupWidth / 2 - wTextWidth / 2, topDimY - 10, wTextWidth, 20);
        ctx.strokeRect(scaledGroupWidth / 2 - wTextWidth / 2, topDimY - 10, wTextWidth, 20);
        ctx.fillStyle = "#111";
        ctx.fillText(widthText, scaledGroupWidth / 2, topDimY + 4);

        // Left Height Dimension Line
        const leftDimX = -dimOffset;
        ctx.beginPath();
        ctx.moveTo(leftDimX, 0);
        ctx.lineTo(leftDimX, groupVisualHeight);
        ctx.stroke();

        // Ticks
        ctx.beginPath();
        ctx.moveTo(leftDimX - 8, 0);
        ctx.lineTo(leftDimX + 8, 0);
        ctx.moveTo(leftDimX - 8, groupVisualHeight);
        ctx.lineTo(leftDimX + 8, groupVisualHeight);
        ctx.stroke();

        // Height Label Box (Rotated)
        ctx.save();
        ctx.translate(leftDimX, groupVisualHeight / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = "white";
        ctx.font = "bold 13px Arial";
        ctx.textAlign = "center";
        const heightText = `${mmHeight} mm`;
        const hTextWidth = ctx.measureText(heightText).width + 16;
        ctx.fillRect(-hTextWidth / 2, -10, hTextWidth, 20);
        ctx.strokeRect(-hTextWidth / 2, -10, hTextWidth, 20);
        ctx.fillStyle = "#111";
        ctx.fillText(heightText, 0, 4);
        ctx.restore();

        ctx.restore(); // Restore margin translation

        return offCanvas.toDataURL("image/png");
      } catch (err) {
        console.error("Error generating cabinet diagram image:", err);
        return null;
      }
    },
    [getBausteinLayout, getCachedImage, getElementColor],
  );

  const drawBaustein = useCallback(
    (ctx, baustein, zoom, dropIndicator) => {
      const { x, y, width, height, id, category } = baustein;
      const isNormal = category === "normale";

      if (!isNormal) {
        ctx.fillStyle = "#f8f8f8";
        ctx.fillRect(x, y, width, height);
        ctx.strokeStyle = "#333";
        ctx.lineWidth = 2 / zoom;
        ctx.strokeRect(x, y, width, height);
      }

      const {
        F,
        S,
        maxSlots,
        railHeight,
        compMargin,
        compPadding,
        compH,
        compWidth,
        compColor,
        spaceBetween,
        compYBase,
      } = getBausteinLayout(baustein);

      // Top rail
      ctx.fillStyle = selectedBaustein === baustein.id ? "#ffd54f" : "white";
      ctx.fillRect(x, y, width, railHeight);
      ctx.strokeStyle = "black";
      ctx.lineWidth = 1 / zoom;
      ctx.strokeRect(x, y, width, railHeight);

      // Corner crosshairs
      const drawCorner = (cx, cy) => {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.scale(1 / HORIZONTAL_OPTICAL_SCALE, 1);
        const radius = 8.5;
        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, 2 * Math.PI);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(-radius - 2, 0);
        ctx.lineTo(radius + 2, 0);
        ctx.moveTo(0, -radius - 2);
        ctx.lineTo(0, radius + 2);
        ctx.stroke();
        ctx.restore();
      };

      drawCorner(x + 12.5, y + 12.5);
      drawCorner(x + width - 12.5, y + 12.5);

      // Main compartments based on F (height) and S (width)
      for (let r = 0; r < F; r++) {
        for (let c = 0; c < S; c++) {
          ctx.fillStyle = compColor;
          ctx.fillRect(
            x + compMargin + c * (compWidth + compMargin),
            compYBase +
              (F === 1 ? spaceBetween / 2 : 0) +
              r * (compH + spaceBetween),
            compWidth,
            compH,
          );
          ctx.strokeRect(
            x + compMargin + c * (compWidth + compMargin),
            compYBase +
              (F === 1 ? spaceBetween / 2 : 0) +
              r * (compH + spaceBetween),
            compWidth,
            compH,
          );
        }
      }

      // Bottom rail
      const bottomY = y + height - railHeight;
      ctx.fillStyle = "white";
      ctx.fillRect(x, bottomY, width, railHeight);
      ctx.strokeRect(x, bottomY, width, railHeight);

      drawCorner(x + 12.5, bottomY + 12.5);
      drawCorner(x + width - 12.5, bottomY + 12.5);

      // Side rails
      ctx.beginPath();
      ctx.moveTo(x, y + railHeight);
      ctx.lineTo(x, bottomY);
      ctx.moveTo(x + width, y + railHeight);
      ctx.lineTo(x + width, bottomY);
      ctx.stroke();

      // Entfernen-Button (nur im Schränke-Modus)
      if (mode === "s") {
        const buttonSize = 16 / zoom;
        const buttonX = x + width - buttonSize - 2;
        const buttonY = y + 2;

        ctx.save();
        ctx.translate(buttonX, buttonY);
        ctx.scale(1 / HORIZONTAL_OPTICAL_SCALE, 1);
        ctx.fillStyle = "#ff4444";
        ctx.fillRect(0, 0, buttonSize, buttonSize);
        ctx.strokeStyle = "#cc0000";
        ctx.lineWidth = 1 / zoom;
        ctx.strokeRect(0, 0, buttonSize, buttonSize);

        ctx.strokeStyle = "white";
        ctx.lineWidth = 2 / zoom;
        const margin = 4 / zoom;
        ctx.beginPath();
        ctx.moveTo(margin, margin);
        ctx.lineTo(buttonSize - margin, buttonSize - margin);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(buttonSize - margin, margin);
        ctx.lineTo(margin, buttonSize - margin);
        ctx.stroke();
        ctx.restore();
      }

      // Highlight selected baustein - REMOVED border logic per user request
      // if (mode === "e" && selectedBaustein === baustein.id) {
      //   ctx.strokeStyle = "#ff6600";
      //   ctx.lineWidth = 3 / zoom;
      //   ctx.strokeRect(x - 2, y - 2, width + 4, height + 4);
      // }

      // Baustein ID and name centered in top rail
      ctx.fillStyle = "#333";
      ctx.textAlign = "center";

      ctx.save();
      ctx.translate(x + width / 2, y + 16);
      ctx.scale(1 / HORIZONTAL_OPTICAL_SCALE, 1);
      ctx.font = "bold 11px Arial";
      ctx.fillText(`${baustein.catalogId} - ${baustein.id}`, 0, 0);
      ctx.restore();

      ctx.textAlign = "left";

      // Draw elements in compartments
      const elements = bausteinElements[baustein.id] || [];

      for (let r = 0; r < F; r++) {
        for (let c = 0; c < S; c++) {
          const compIndex = r * S + c;
          const compX = x + compMargin + c * (compWidth + compMargin);
          const compYPos =
            compYBase +
            (F === 1 ? spaceBetween / 2 : 0) +
            r * (compH + spaceBetween);

          // Draw elements that belong to this compartment
          const elementsInComp = elements.filter(
            (el) => (el.compIndex || 0) === compIndex,
          );

          if (mode === "s") ctx.globalAlpha = 0.3;
          elementsInComp.forEach((element, i) => {
            if (element && !element.isMoving) {
              const slot = element.slot !== undefined ? element.slot : i;
              const elemX =
                compX +
                compPadding +
                (slot / maxSlots) * (compWidth - 2 * compPadding);
              let elemHeight = element.klemmen ? compH + 30 : compH;
              let elemWidth =
                (element.size / maxSlots) * (compWidth - 2 * compPadding);
              const elemY = compYPos + (compH - elemHeight) / 2;

              const sources = getElementImageSource(element);
              const version = element.image
                ? element.image.split("_").pop().split(".")[0]
                : "";
              const cachedImg = getCachedImage(sources, version);

              if (cachedImg instanceof HTMLImageElement) {
                ctx.drawImage(cachedImg, elemX, elemY, elemWidth, elemHeight);
              } else if (cachedImg === "loading") {
                // Show a skeleton or light background while loading
                ctx.fillStyle = "rgba(0, 0, 0, 0.05)";
                ctx.fillRect(elemX, elemY, elemWidth, elemHeight);

                // Animated loading indicator
                ctx.strokeStyle = "rgba(251, 191, 11, 0.2)";
                ctx.lineWidth = 1 / zoom;
                ctx.strokeRect(
                  elemX + 2 / zoom,
                  elemY + 2 / zoom,
                  elemWidth - 4 / zoom,
                  elemHeight - 4 / zoom,
                );
              } else {
                // Fallback if no image exists after all attempts
                ctx.fillStyle = getElementColor(element.size);
                ctx.globalAlpha = 0.6; // Make non-image elements a bit more subtle if that helps
                ctx.fillRect(elemX, elemY, elemWidth, elemHeight);
                ctx.globalAlpha = 1.0;

                // Draw text as fallback if element has no image
                ctx.fillStyle = "#666";
                ctx.font = `${Math.min(8 / zoom, elemWidth / 2)}px Arial`;
                ctx.textAlign = "center";
                ctx.fillText(
                  element.id,
                  elemX + elemWidth / 2,
                  elemY + elemHeight / 2,
                );
              }

              // Thin inner border
              ctx.strokeStyle = "rgba(0, 0, 0, 0.2)";
              ctx.lineWidth = 1 / zoom;
              ctx.strokeRect(
                elemX + 0.5,
                elemY + 0.5,
                elemWidth - 1,
                elemHeight - 1,
              );

              // Element remove button (only in element mode and when hovered)
              const isHovered =
                hoveredElement &&
                hoveredElement.bausteinId === baustein.id &&
                hoveredElement.elementIndex === elements.indexOf(element);

              if (mode === "e" && isHovered) {
                const eButtonSize = 18 / zoom;
                const eButtonX = elemX + (elemWidth - eButtonSize) / 2;
                const eButtonY = elemY - 6 / zoom;

                ctx.save();
                ctx.translate(eButtonX, eButtonY);
                ctx.scale(1 / HORIZONTAL_OPTICAL_SCALE, 1);
                ctx.fillStyle = "#ff4444";
                ctx.fillRect(0, 0, eButtonSize, eButtonSize);
                ctx.strokeStyle = "#cc0000";
                ctx.lineWidth = 1 / zoom;
                ctx.strokeRect(0, 0, eButtonSize, eButtonSize);

                ctx.strokeStyle = "white";
                ctx.lineWidth = 1.5 / zoom;
                const margin = 3 / zoom;
                ctx.beginPath();
                ctx.moveTo(margin, margin);
                ctx.lineTo(eButtonSize - margin, eButtonSize - margin);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(eButtonSize - margin, margin);
                ctx.lineTo(margin, eButtonSize - margin);
                ctx.stroke();
                ctx.restore();
              }

              // Removed size label
              ctx.textAlign = "left";
            }
          });
          ctx.globalAlpha = 1.0;
        }
      }

      // Draw drop indicator
      if (dropIndicator && dropIndicator.bausteinId === id) {
        if (dropIndicator.isValid && dropIndicator.previewElements) {
          const { compIndex } = dropIndicator;
          const r = Math.floor(compIndex / S);
          const c = compIndex % S;

          const compX = x + compMargin + c * (compWidth + compMargin);
          const compYPos =
            compYBase +
            (F === 1 ? spaceBetween / 2 : 0) +
            r * (compH + spaceBetween);

          // Draw the "ghost" preview of all elements as they would be after reorganization
          dropIndicator.previewElements.forEach((el) => {
            const sources = getElementImageSource(el);
            const version = el.image
              ? el.image.split("_").pop().split(".")[0]
              : "";
            const cachedImg = getCachedImage(sources, version);

            let elemHeight = el.klemmen ? compH + 30 : compH;
            const slot = el.slot;
            let elemWidth =
              (el.size / maxSlots) * (compWidth - 2 * compPadding);

            const elemX =
              compX +
              compPadding +
              (slot / maxSlots) * (compWidth - 2 * compPadding);
            const elemY = compYPos + (compH - elemHeight) / 2;

            ctx.globalAlpha = el.isMoving ? 0.6 : 0.3;

            if (cachedImg instanceof HTMLImageElement) {
              ctx.drawImage(cachedImg, elemX, elemY, elemWidth, elemHeight);
            } else {
              ctx.fillStyle = getElementColor(el.size);
              ctx.fillRect(elemX, elemY, elemWidth, elemHeight);
            }

            ctx.strokeStyle = "rgba(0, 0, 0, 0.1)"; // Lighter for ghost
            ctx.lineWidth = 1 / zoom;
            ctx.strokeRect(
              elemX + 0.5,
              elemY + 0.5,
              elemWidth - 1,
              elemHeight - 1,
            );

            ctx.globalAlpha = 1.0;
          });
        } else {
          ctx.fillStyle = "rgba(255, 0, 0, 0.5)";
          ctx.fillRect(
            dropIndicator.x,
            dropIndicator.y,
            dropIndicator.width,
            dropIndicator.height,
          );
        }
      }
    },
    [
      bausteinElements,
      getCachedImage,
      getElementColor,
      getBausteinLayout,
      hoveredElement,
      mode,
      selectedBaustein,
    ],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.translate(pan.x, pan.y);
      ctx.scale(zoom * HORIZONTAL_OPTICAL_SCALE, zoom);

      // Draw grid covering complete canvas
      const canvasWidth = canvas.width / (zoom * HORIZONTAL_OPTICAL_SCALE);
      const canvasHeight = canvas.height / zoom;
      const startX =
        Math.floor(-pan.x / (zoom * HORIZONTAL_OPTICAL_SCALE) / GRID_SIZE) *
        GRID_SIZE;
      const startY = Math.floor(-pan.y / zoom / GRID_SIZE) * GRID_SIZE;
      const endX = startX + canvasWidth + GRID_SIZE;
      const endY = startY + canvasHeight + GRID_SIZE;

      ctx.fillStyle = "#e0e0e0";

      for (let x = startX; x <= endX; x += GRID_SIZE) {
        for (let y = startY; y <= endY; y += GRID_SIZE) {
          ctx.beginPath();
          ctx.arc(x, y, 1 / zoom, 0, 2 * Math.PI);
          ctx.fill();
        }
      }

      // Always draw the group box
      const groupVisualWidth = groupWidth * 250;
      const groupVisualHeight = groupHeight * 150;
      const groupX = 0;
      const groupY = 0;
      const borderWidth = 32;
      ctx.strokeStyle = "#ccc";
      ctx.lineWidth = borderWidth;
      // Stroke outside: center the 32px line 16px away from the edge
      const offset = borderWidth / 2;
      ctx.strokeRect(
        groupX - offset,
        groupY - offset,
        groupVisualWidth + 2 * offset,
        groupVisualHeight + 2 * offset,
      );

      // Fine markup (grid) inside the group area
      ctx.save();
      ctx.setLineDash([5, 5]);
      ctx.strokeStyle = "rgba(0, 0, 0, 0.15)";
      ctx.lineWidth = 1 / zoom;

      // Vertical unit lines (every 250mm)
      ctx.textAlign = "center";
      ctx.font = "10px Arial";
      ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
      for (let i = 1; i < groupWidth; i++) {
        const x = groupX + i * 250;
        ctx.beginPath();
        ctx.moveTo(x, groupY);
        ctx.lineTo(x, groupY + groupVisualHeight);
        ctx.stroke();

        ctx.save();
        ctx.translate(x, groupY + groupVisualHeight - 5);
        ctx.scale(1 / HORIZONTAL_OPTICAL_SCALE, 1);
        ctx.fillText("250", 0, 0);
        ctx.restore();
      }

      // Horizontal unit lines (every 150mm)
      ctx.textAlign = "left";
      for (let i = 1; i < groupHeight; i++) {
        const y = groupY + i * 150;
        ctx.beginPath();
        ctx.moveTo(groupX, y);
        ctx.lineTo(groupX + groupVisualWidth, y);
        ctx.stroke();

        ctx.save();
        ctx.translate(groupX + 5, y - 5);
        ctx.scale(1 / HORIZONTAL_OPTICAL_SCALE, 1);
        ctx.fillText("150", 0, 0);
        ctx.restore();
      }
      ctx.restore();

      // Dimension labels
      ctx.fillStyle = "#666";
      ctx.font = "14px Arial";
      ctx.textAlign = "center";

      const dimOffset = 80; // Distance from group edge to dimension line

      // Top Dimension (Width)
      const topDimY = groupY - dimOffset;
      const mmWidth = groupWidth * 250;

      // Horizontal line
      ctx.beginPath();
      ctx.moveTo(groupX, topDimY);
      ctx.lineTo(groupX + groupVisualWidth, topDimY);
      ctx.strokeStyle = "#888";
      ctx.lineWidth = 1 / zoom;
      ctx.stroke();

      // Tick marks
      ctx.beginPath();
      ctx.moveTo(groupX, topDimY - 10);
      ctx.lineTo(groupX, topDimY + 10);
      ctx.moveTo(groupX + groupVisualWidth, topDimY - 10);
      ctx.lineTo(groupX + groupVisualWidth, topDimY + 10);
      ctx.stroke();

      // Label
      ctx.save();
      ctx.translate(groupX + groupVisualWidth / 2, topDimY);
      ctx.scale(1 / HORIZONTAL_OPTICAL_SCALE, 1);
      ctx.fillStyle = "white";
      const widthText = `${mmWidth} mm`;
      const textWidth = ctx.measureText(widthText).width + 10;
      ctx.fillRect(-textWidth / 2, -10, textWidth, 20);
      ctx.fillStyle = "#333";
      ctx.fillText(widthText, 0, 5);
      ctx.restore();

      // Left Dimension (Height)
      const leftDimX = groupX - dimOffset;
      const mmHeight = groupHeight * 150;

      // Vertical line
      ctx.beginPath();
      ctx.moveTo(leftDimX, groupY);
      ctx.lineTo(leftDimX, groupY + groupVisualHeight);
      ctx.stroke();

      // Tick marks
      ctx.beginPath();
      ctx.moveTo(leftDimX - 10, groupY);
      ctx.lineTo(leftDimX + 10, groupY);
      ctx.moveTo(leftDimX - 10, groupY + groupVisualHeight);
      ctx.lineTo(leftDimX + 10, groupY + groupVisualHeight);
      ctx.stroke();

      // Label (rotated)
      ctx.save();
      ctx.translate(leftDimX, groupY + groupVisualHeight / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.scale(1, 1 / HORIZONTAL_OPTICAL_SCALE);
      ctx.fillStyle = "white";
      const heightText = `${mmHeight} mm`;
      const textHeight = ctx.measureText(heightText).width + 10;
      ctx.fillRect(-textHeight / 2, -10, textHeight, 20);
      ctx.fillStyle = "#333";
      ctx.fillText(heightText, 0, 5);
      ctx.restore();

      // Name/element box for the group
      // nameBoxHeight unused
      // nameBoxY unused because code below is commented out
      // const nameBoxY = groupY - nameBoxHeight - 5 / zoom;
      // ctx.fillStyle = "white";
      // ctx.fillRect(groupX, nameBoxY, groupVisualWidth, nameBoxHeight);
      // ctx.strokeStyle = "black";
      // ctx.lineWidth = 1 / zoom;
      // ctx.strokeRect(groupX, nameBoxY, groupVisualWidth, nameBoxHeight);

      // ctx.fillStyle = "#333";
      // ctx.textAlign = "center";
      // ctx.font = "bold 16px Arial";
      // ctx.fillText(groupName, groupX + groupVisualWidth / 2, nameBoxY + nameBoxHeight / 2 + 6);
      // ctx.textAlign = "left";

      placedBausteine.forEach((baustein) => {
        drawBaustein(ctx, baustein, zoom, dropIndicator);
      });

      // Draw hovered element tooltip last (z-index: 100 simulation)
      if (mode === "e" && hoveredElement) {
        const baustein = placedBausteine.find(
          (b) => b.id === hoveredElement.bausteinId,
        );
        if (baustein) {
          const elements = bausteinElements[baustein.id] || [];
          const element = elements[hoveredElement.elementIndex];
          if (element) {
            const layout = getBausteinLayout(baustein);
            const {
              F,
              S,
              maxSlots,
              compMargin,
              compPadding,
              compH,
              compWidth,
              spaceBetween,
              compYBase,
            } = layout;
            const compIndex = element.compIndex || 0;
            const r = Math.floor(compIndex / S);
            const c = compIndex % S;

            const compX =
              baustein.x + compMargin + c * (compWidth + compMargin);
            const compYPos =
              compYBase +
              (F === 1 ? spaceBetween / 2 : 0) +
              r * (compH + spaceBetween);

            let elemHeight = element.klemmen ? compH + 30 : compH;
            let elemWidth =
              (element.size / maxSlots) * (compWidth - 2 * compPadding);

            const slot = element.slot !== undefined ? element.slot : 0;
            const elemX =
              compX +
              compPadding +
              (slot / maxSlots) * (compWidth - 2 * compPadding);
            const elemY = compYPos + (compH - elemHeight) / 2;

            ctx.save();
            const tooltipFontSize = 12 / zoom;
            const headerFontSize = 14 / zoom;

            const lines = [
              {
                text: element.name,
                font: `bold ${headerFontSize}px "Inter", "Roboto", "Arial", sans-serif`,
                color: "#fbbf0b",
              },
              {
                text: `ID: ${element.id}`,
                font: `${tooltipFontSize}px "Inter", "Roboto", "Arial", sans-serif`,
                color: "#ddd",
              },
              {
                text: `Größe: ${element.size} PLE   |   Typ: ${element.type}`,
                font: `${tooltipFontSize}px "Inter", "Roboto", "Arial", sans-serif`,
                color: "#ddd",
              },
              {
                text: `Kategorie: ${element.category}`,
                font: `${tooltipFontSize}px "Inter", "Roboto", "Arial", sans-serif`,
                color: "#ddd",
              },
              settings.showprices && {
                text: `Preis: ${element.price.toFixed(2)} €`,
                font: `bold ${tooltipFontSize}px "Inter", "Roboto", "Arial", sans-serif`,
                color: "#ffffff",
              },
            ].filter(Boolean);

            if (element.description) {
              lines.push({
                text: element.description,
                font: `italic ${tooltipFontSize - 1}px "Inter", "Roboto", "Arial", sans-serif`,
                color: "#aaaaaa",
              });
            }

            let maxTextWidth = 0;
            lines.forEach((line) => {
              ctx.font = line.font;
              maxTextWidth = Math.max(
                maxTextWidth,
                ctx.measureText(line.text).width,
              );
            });

            const padding = 12 / zoom;
            const lineHeight = 18 / zoom;
            const tooltipWidth = maxTextWidth + padding * 2;
            const tooltipHeight = lines.length * lineHeight + padding;

            const tooltipY = elemY + elemHeight + 6 / zoom;
            const centerX = elemX + elemWidth / 2;

            // Undo horizontal scale stretch so fonts and background are uniform
            ctx.translate(centerX, tooltipY);
            ctx.scale(1 / HORIZONTAL_OPTICAL_SCALE, 1);

            const drawX = -tooltipWidth / 2;

            // Draw shadow
            ctx.shadowColor = "rgba(0, 0, 0, 0.6)";
            ctx.shadowBlur = 12 / zoom;
            ctx.shadowOffsetY = 4 / zoom;

            // Draw background with rounded corners
            ctx.fillStyle = "rgba(24, 24, 27, 0.95)"; // Very dark grey, slightly transparent
            ctx.beginPath();
            const radius = 6 / zoom;
            if (ctx.roundRect) {
              ctx.roundRect(drawX, 0, tooltipWidth, tooltipHeight, radius);
            } else {
              ctx.rect(drawX, 0, tooltipWidth, tooltipHeight);
            }
            ctx.fill();

            // Remove shadow for text
            ctx.shadowBlur = 0;
            ctx.shadowOffsetY = 0;

            // Draw text
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            lines.forEach((line, idx) => {
              ctx.fillStyle = line.color;
              ctx.font = line.font;
              ctx.fillText(
                line.text,
                drawX + padding,
                padding + idx * lineHeight + lineHeight / 2, // aligned middle
              );
            });
            ctx.restore();
          }
        }
      }

      if (draggedPlacedElementInfo && draggedElementPosition) {
        // Draw the dragged element at the mouse cursor
        const { element } = draggedPlacedElementInfo;
        const { x, y } = draggedElementPosition;

        // Use standard width based on common baustein layout for the preview
        const elemWidth = element.size * 18;
        const elemHeight = element.klemmen ? 88 : 58;

        ctx.globalAlpha = 0.7; // Make it semi-transparent

        const sources = getElementImageSource(element);
        const version = element.image
          ? element.image.split("_").pop().split(".")[0]
          : "";
        const cachedImg = getCachedImage(sources, version);

        if (cachedImg instanceof HTMLImageElement) {
          ctx.drawImage(
            cachedImg,
            x - elemWidth / 2,
            y - elemHeight / 2,
            elemWidth,
            elemHeight,
          );
        } else {
          ctx.fillStyle = getElementColor(element.size);
          ctx.fillRect(
            x - elemWidth / 2,
            y - elemHeight / 2,
            elemWidth,
            elemHeight,
          );
        }

        ctx.strokeStyle = "rgba(0, 0, 0, 0.3)";
        ctx.lineWidth = 1 / zoom;
        ctx.strokeRect(
          x - elemWidth / 2 + 0.5,
          y - elemHeight / 2 + 0.5,
          elemWidth - 1,
          elemHeight - 1,
        );

        ctx.globalAlpha = 1.0;
        ctx.textAlign = "left";
      }

      ctx.restore();
    };

    draw();
  }, [
    zoom,
    pan,
    placedBausteine,
    mode,
    bausteinElements,
    selectedBaustein,
    groupName,
    draggedPlacedElementInfo,
    draggedElementPosition,
    dropIndicator,
    hoveredElement,
    groupWidth,
    groupHeight,
    windowSize,
    drawBaustein,
    getBausteinLayout,
    getCachedImage,
    getElementColor,
    settings,
    updateTrigger,
  ]);

  const handleWheel = (e) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.1, Math.min(5, zoom * zoomFactor));

    const zoomChange = newZoom / zoom;
    setPan({
      x: mouseX - (mouseX - pan.x) * zoomChange,
      y: mouseY - (mouseY - pan.y) * zoomChange,
    });
    setZoom(newZoom);
  };

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - pan.x) / (zoom * HORIZONTAL_OPTICAL_SCALE),
      y: (e.clientY - rect.top - pan.y) / zoom,
    };
  };

  const getBausteinAt = useCallback(
    (x, y) => {
      for (let i = placedBausteine.length - 1; i >= 0; i--) {
        const baustein = placedBausteine[i];
        if (
          x >= baustein.x &&
          x <= baustein.x + baustein.width &&
          y >= baustein.y &&
          y <= baustein.y + baustein.height
        ) {
          return { baustein, index: i };
        }
      }
      return null;
    },
    [placedBausteine],
  );

  const getElementInsertionDetails = useCallback(
    (dropX, dropY, baustein, elementSize = 1) => {
      const bausteinX = baustein.x;
      const {
        F,
        S,
        maxSlots,
        compMargin,
        compPadding,
        compH,
        compWidth,
        spaceBetween,
        compYBase,
      } = getBausteinLayout(baustein);

      // Find which compartment the mouse is over
      let bestComp = { r: 0, c: 0, index: 0 };
      let minDist = Infinity;

      for (let r = 0; r < F; r++) {
        for (let c = 0; c < S; c++) {
          const rectX = bausteinX + compMargin + c * (compWidth + compMargin);
          const rectY =
            compYBase +
            (F === 1 ? spaceBetween / 2 : 0) +
            r * (compH + spaceBetween);

          // Calculate distance to the center of this compartment
          const centerX = rectX + compWidth / 2;
          const centerY = rectY + compH / 2;
          const dist = Math.sqrt(
            Math.pow(dropX - centerX, 2) + Math.pow(dropY - centerY, 2),
          );

          if (dist < minDist) {
            minDist = dist;
            bestComp = { r, c, index: r * S + c, rectX, rectY };
          }
        }
      }

      const { rectX, rectY, index: compIndex } = bestComp;
      const innerWidth = compWidth - 2 * compPadding;
      const slotWidth = innerWidth / maxSlots;

      // Center the element on the drop point
      let slot =
        Math.floor(
          (dropX - (rectX + compPadding) - (elementSize * slotWidth) / 2) /
            (slotWidth / 4),
        ) * 0.25;
      slot = Math.max(0, Math.min(maxSlots - elementSize, slot));

      const placeholderHeight =
        elementSize !== undefined &&
        elementSize > 0 &&
        baustein.category === "klemmen"
          ? compH + 30
          : compH;

      return {
        slot,
        compIndex,
        placeholderX: rectX + compPadding + slot * slotWidth,
        placeholderY: rectY + (compH - placeholderHeight) / 2,
        placeholderWidth: (elementSize / maxSlots) * innerWidth,
        placeholderHeight: placeholderHeight,
      };
    },
    [getBausteinLayout],
  );

  const getElementAt = (x, y) => {
    for (let i = placedBausteine.length - 1; i >= 0; i--) {
      const baustein = placedBausteine[i];
      const { id } = baustein;
      const { F, S, maxSlots, spaceBetween, compYBase } =
        getBausteinLayout(baustein);

      const bausteineInBaustein = bausteinElements[id] || [];

      for (let r = 0; r < F; r++) {
        for (let c = 0; c < S; c++) {
          const { compMargin, compPadding, compH, compWidth } =
            getBausteinLayout(baustein);
          const compIndex = r * S + c;
          const compX = baustein.x + compMargin + c * (compWidth + compMargin);
          const compYPos =
            compYBase +
            (F === 1 ? spaceBetween / 2 : 0) +
            r * (compH + spaceBetween);

          const elementsInComp = bausteineInBaustein.filter(
            (el) => (el.compIndex || 0) === compIndex,
          );

          for (let j = 0; j < elementsInComp.length; j++) {
            const element = elementsInComp[j];
            const slot = element.slot !== undefined ? element.slot : j;
            const elemX =
              compX +
              compPadding +
              (slot / maxSlots) * (compWidth - 2 * compPadding);
            const elemWidth =
              (element.size / maxSlots) * (compWidth - 2 * compPadding);
            const elemHeight = element.klemmen ? compH + 30 : compH;
            const elemY = compYPos + (compH - elemHeight) / 2;

            if (
              x >= elemX &&
              x <= elemX + elemWidth &&
              y >= elemY &&
              y <= elemY + elemHeight
            ) {
              // We need the index in the original elementsInBaustein array
              const originalIndex = bausteineInBaustein.indexOf(element);
              return {
                bausteinId: id,
                elementIndex: originalIndex,
                element: element,
              };
            }
          }
        }
      }
    }
    return null;
  };

  const getBausteinTopRailAt = (x, y) => {
    for (let i = placedBausteine.length - 1; i >= 0; i--) {
      const baustein = placedBausteine[i];
      const railHeight = 25;
      if (
        x >= baustein.x &&
        x <= baustein.x + baustein.width &&
        y >= baustein.y &&
        y <= baustein.y + railHeight
      ) {
        return { baustein, index: i };
      }
    }
    return null;
  };

  const getRemoveButtonAt = (x, y) => {
    for (let i = placedBausteine.length - 1; i >= 0; i--) {
      const baustein = placedBausteine[i];
      const buttonSize = 16 / zoom;
      const buttonX = baustein.x + baustein.width - buttonSize - 2;
      const buttonY = baustein.y + 2;

      if (
        x >= buttonX &&
        x <= buttonX + buttonSize &&
        y >= buttonY &&
        y <= buttonY + buttonSize
      ) {
        return i;
      }
    }
    return null;
  };

  const getElementRemoveButtonAt = (x, y) => {
    if (mode !== "e") return null;

    for (let i = placedBausteine.length - 1; i >= 0; i--) {
      const baustein = placedBausteine[i];
      const { id } = baustein;
      const { F, S, maxSlots, spaceBetween, compYBase } =
        getBausteinLayout(baustein);

      const bausteineInBaustein = bausteinElements[id] || [];

      for (let r = 0; r < F; r++) {
        for (let c = 0; c < S; c++) {
          const { compMargin, compPadding, compH, compWidth } =
            getBausteinLayout(baustein);
          const compIndex = r * S + c;
          const compX = baustein.x + compMargin + c * (compWidth + compMargin);
          const compYPos =
            compYBase +
            (F === 1 ? spaceBetween / 2 : 0) +
            r * (compH + spaceBetween);

          const elementsInComp = bausteineInBaustein.filter(
            (el) => (el.compIndex || 0) === compIndex,
          );

          for (let j = 0; j < elementsInComp.length; j++) {
            const element = elementsInComp[j];
            const slot = element.slot !== undefined ? element.slot : j;
            const elemX =
              compX +
              compPadding +
              (slot / maxSlots) * (compWidth - 2 * compPadding);
            const elemWidth =
              (element.size / maxSlots) * (compWidth - 2 * compPadding);
            const elemHeight = element.klemmen ? compH + 30 : compH;
            const elemY = compYPos + (compH - elemHeight) / 2;

            const eButtonSize = 18 / zoom;
            const eButtonX = elemX + (elemWidth - eButtonSize) / 2;
            const eButtonY = elemY - 6 / zoom;

            if (
              x >= eButtonX &&
              x <= eButtonX + eButtonSize &&
              y >= eButtonY &&
              y <= eButtonY + eButtonSize
            ) {
              return {
                bausteinId: id,
                elementIndex: bausteineInBaustein.indexOf(element),
              };
            }
          }
        }
      }
    }
    return null;
  };

  const addElementToBaustein = React.useCallback(
    (
      element,
      targetBausteinId = null,
      targetSlot = null,
      targetCompIndex = 0,
    ) => {
      let startBausteinId =
        targetBausteinId ||
        selectedBaustein ||
        (placedBausteine.length > 0 ? placedBausteine[0].id : null);

      if (!startBausteinId) {
        triggerAlert("Bitte platzieren Sie zuerst einen Baustein!");
        return;
      }

      let elementWithId = element;
      if (!element.catalogId) {
        const serialId = `E${String(nextElementSerial).padStart(4, "0")}`;
        setNextElementSerial((prev) => prev + 1);
        elementWithId = {
          ...element,
          catalogId: element.id,
          id: serialId,
        };
      }

      const checkSpot = (cabId, cIndex) => {
        const baustein = placedBausteine.find((c) => c.id === cabId);
        if (!baustein) return false;
        const { maxSlots } = getBausteinSpecs(baustein);
        const allEls = bausteinElements[cabId] || [];
        const inComp = allEls.filter((el) => (el.compIndex || 0) === cIndex);
        const currentSize = inComp.reduce((sum, el) => sum + el.size, 0);
        return currentSize + elementWithId.size <= maxSlots;
      };

      let bestFound = null;

      // Search through bausteine starting from the target one
      const startIndex = Math.max(
        0,
        placedBausteine.findIndex((c) => c.id === startBausteinId),
      );

      for (let i = 0; i < placedBausteine.length; i++) {
        const idx = (startIndex + i) % placedBausteine.length;
        const baustein = placedBausteine[idx];
        const cabId = baustein.id;

        const { F, S, maxSlots } = getBausteinSpecs(baustein);
        const totalComps = F * S;

        // In the target baustein, we might want to start from the target compartment
        let startComp = 0;
        if (cabId === targetBausteinId && targetCompIndex !== null) {
          startComp = targetCompIndex;
        }

        for (let j = 0; j < totalComps; j++) {
          const c = (startComp + j) % totalComps;
          if (checkSpot(cabId, c)) {
            // Keep targetSlot only if we are in the exact target baustein and compartment
            let useSlot =
              cabId === targetBausteinId && c === targetCompIndex
                ? targetSlot
                : null;
            bestFound = { cabId, compIndex: c, slot: useSlot, maxSlots };
            break;
          }
        }
        if (bestFound) break;
      }

      if (bestFound) {
        const { cabId, compIndex, slot, maxSlots } = bestFound;
        const allElements = bausteinElements[cabId] || [];
        const otherCompElements = allElements.filter(
          (el) => (el.compIndex || 0) !== compIndex,
        );
        const compElements = allElements.filter(
          (el) => (el.compIndex || 0) === compIndex,
        );

        // If no specific slot, put it at the end of existing elements
        let finalSlot = slot;
        if (finalSlot === null) {
          finalSlot = compElements.reduce((sum, el) => sum + el.size, 0);
        }

        let newCompElements = reorganizeElements(
          compElements,
          {
            ...elementWithId,
            slot: finalSlot,
          },
          maxSlots,
        );

        newCompElements = newCompElements.map((el) => {
          const { isMoving, ...rest } = el;
          return { ...rest, compIndex: compIndex };
        });

        setBausteinElements((prev) => ({
          ...prev,
          [cabId]: [...otherCompElements, ...newCompElements],
        }));
      } else {
        if (draggedPlacedElementInfo) {
          // Return to original spot if no space found anywhere
          const {
            bausteinId: originalBausteinId,
            elementIndex,
            element: originalElement,
          } = draggedPlacedElementInfo;
          setBausteinElements((prev) => {
            const newBausteinElements = { ...prev };
            const elements = [
              ...(newBausteinElements[originalBausteinId] || []),
            ];
            elements.splice(elementIndex, 0, originalElement);
            newBausteinElements[originalBausteinId] = elements;
            return newBausteinElements;
          });
        } else {
          triggerAlert("Kein Platz in verfügbaren Bausteinen!", "error");
        }
      }
    },
    [
      placedBausteine,
      selectedBaustein,
      bausteinElements,
      draggedPlacedElementInfo,
      nextElementSerial,
    ],
  );

  const handleMouseDown = (e) => {
    if (e.button === 1 || e.button === 2) e.preventDefault();
    const coords = getCanvasCoords(e);

    if (mode === "e") {
      if (e.button === 0) {
        const elemRemoveHit = getElementRemoveButtonAt(coords.x, coords.y);
        if (elemRemoveHit) {
          const { bausteinId, elementIndex } = elemRemoveHit;
          const updatedElements = [...(bausteinElements[bausteinId] || [])];
          updatedElements.splice(elementIndex, 1);
          setBausteinElements((prev) => ({
            ...prev,
            [bausteinId]: updatedElements,
          }));
          return;
        }

        const placedElementHit = getElementAt(coords.x, coords.y);

        if (placedElementHit) {
          setDraggedPlacedElementInfo(placedElementHit);
          setDraggedElementPosition(coords);

          // "Pick up" the element by removing it from the state
          const updatedElements = [
            ...(bausteinElements[placedElementHit.bausteinId] || []),
          ];
          updatedElements.splice(placedElementHit.elementIndex, 1);
          setBausteinElements((prev) => ({
            ...prev,
            [placedElementHit.bausteinId]: updatedElements,
          }));
          return;
        }

        const hit = getBausteinTopRailAt(coords.x, coords.y);
        if (hit) {
          setSelectedBaustein(hit.baustein.id);
        } else {
          setSelectedBaustein(null);
        }
      } else if (e.button === 1 || e.button === 2) {
        setIsDragging(true);
        setLastMouse({ x: e.clientX, y: e.clientY });
      }
      return;
    }

    const removeButtonIndex = getRemoveButtonAt(coords.x, coords.y);

    if (e.button === 0 && removeButtonIndex !== null) {
      const bausteinToDelete = placedBausteine[removeButtonIndex];
      const bausteineInBaustein = bausteinElements[bausteinToDelete.id] || [];

      const deleteBaustein = () => {
        const updated = placedBausteine.filter(
          (_, index) => index !== removeButtonIndex,
        );
        setPlacedBausteine(updated);
        // Also cleanup elements
        setBausteinElements((prev) => {
          const newState = { ...prev };
          delete newState[bausteinToDelete.id];
          return newState;
        });
      };

      if (bausteineInBaustein.length > 0) {
        setConfirmation({
          message: `Dieser Baustein enthält ${bausteineInBaustein.length} Element(e). Möchten Sie ihn wirklich löschen?`,
          onConfirm: deleteBaustein,
        });
      } else {
        deleteBaustein();
      }
      return;
    }

    const hit = getBausteinAt(coords.x, coords.y);

    if (e.button === 0 && hit) {
      setSelectedBaustein(hit.baustein.id); // Add selection
      setDraggedBaustein(hit.index);
      setDragOffset({
        x: coords.x - hit.baustein.x,
        y: coords.y - hit.baustein.y,
      });
    } else if (e.button === 1 || e.button === 2) {
      setIsDragging(true);
      setLastMouse({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    const coords = getCanvasCoords(e);
    // Handle dragging a placed element
    if (draggedPlacedElementInfo) {
      setDraggedElementPosition(coords);
    }
    // Handle dragging from sidebar
    else if (draggedElement) {
      // just for cursor style, the main logic is global
    }
    // Handle panning
    else if (isDragging) {
      setPan({
        x: pan.x + (e.clientX - lastMouse.x),
        y: pan.y + (e.clientY - lastMouse.y),
      });
      setLastMouse({ x: e.clientX, y: e.clientY });
      return; // prevent other logic while panning
    } else if (mode === "s" && draggedBaustein !== null) {
      const snappedX = snapToGrid(coords.x - dragOffset.x);
      const snappedY = snapToGrid(coords.y - dragOffset.y);
      const current = placedBausteine[draggedBaustein];

      // Constrain to cabinet boundaries (0,0 to groupWidth*250, groupHeight*150)
      const maxX = groupWidth * 250 - current.width;
      const maxY = groupHeight * 150 - current.height;
      const constrainedX = Math.max(0, Math.min(snappedX, maxX));
      const constrainedY = Math.max(0, Math.min(snappedY, maxY));

      const newBaustein = {
        ...current,
        x: constrainedX,
        y: constrainedY,
      };

      if (!checkCollision(newBaustein, draggedBaustein)) {
        const updated = [...placedBausteine];
        updated[draggedBaustein] = newBaustein;
        setPlacedBausteine(updated);
      }
      return;
    }

    // Update hovered element state for remove button visibility
    if (
      mode === "e" &&
      !draggedBaustein &&
      !isDragging &&
      !draggedPlacedElementInfo &&
      !draggedElement
    ) {
      const elementHit = getElementAt(coords.x, coords.y);
      if (elementHit) {
        setHoveredElement({
          bausteinId: elementHit.bausteinId,
          elementIndex: elementHit.elementIndex,
        });
      } else {
        setHoveredElement(null);
      }
    } else if (hoveredElement) {
      setHoveredElement(null);
    }

    // Update drop indicator if dragging an element
    if (draggedElement || draggedPlacedElementInfo) {
      const bausteinHit = getBausteinAt(coords.x, coords.y);
      if (bausteinHit) {
        const element =
          draggedElement ||
          (draggedPlacedElementInfo ? draggedPlacedElementInfo.element : null);
        if (element) {
          const { baustein } = bausteinHit;
          const { maxSlots } = getBausteinSpecs(baustein);
          const details = getElementInsertionDetails(
            coords.x,
            coords.y,
            baustein,
            element.size,
          );

          const currentElements = bausteinElements[baustein.id] || [];
          const compElements = currentElements.filter(
            (el) => (el.compIndex || 0) === details.compIndex,
          );
          const totalSizeInComp =
            compElements.reduce((sum, el) => sum + el.size, 0) + element.size;
          const isValid = totalSizeInComp <= maxSlots;

          let displaySlot = details.slot;
          let previewElements = [];
          if (isValid) {
            // Calculate where all elements WOULD end up in this compartment
            previewElements = reorganizeElements(
              compElements,
              { ...element, slot: details.slot },
              maxSlots,
            );
            const movingInTemp = previewElements.find((el) => el.isMoving);
            if (movingInTemp) displaySlot = movingInTemp.slot;
          }

          setDropIndicator({
            bausteinId: baustein.id,
            x: details.placeholderX,
            y: details.placeholderY,
            width: details.placeholderWidth,
            height: details.placeholderHeight,
            slot: displaySlot,
            compIndex: details.compIndex,
            isValid: isValid,
            previewElements: previewElements,
          });
        }
      } else {
        setDropIndicator(null);
      }
    }
  };

  // Add global event listeners for element dragging
  useEffect(() => {
    const handleGlobalMouseUp = (e) => {
      if (draggedElement && draggedFromSidebar) {
        setDropIndicator(null);
        // Check if mouse is over canvas
        const canvas = canvasRef.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          if (
            e.clientX >= rect.left &&
            e.clientX <= rect.right &&
            e.clientY >= rect.top &&
            e.clientY <= rect.bottom
          ) {
            const coords = getCanvasCoords(e);
            const hit = getBausteinAt(coords.x, coords.y);

            if (hit) {
              const details = getElementInsertionDetails(
                coords.x,
                coords.y,
                hit.baustein,
                draggedElement.size,
              );
              addElementToBaustein(
                draggedElement,
                hit.baustein.id,
                details.slot,
                details.compIndex,
              );
            }
          }
        }
        setDraggedElement(null);
        setDraggedFromSidebar(false);
      }
    };

    if (draggedElement && draggedFromSidebar) {
      document.addEventListener("mouseup", handleGlobalMouseUp);
      return () => {
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [
    draggedElement,
    draggedFromSidebar,
    pan,
    zoom,
    placedBausteine,
    bausteinElements,
    addElementToBaustein,
    getBausteinAt,
    getElementInsertionDetails,
  ]);

  const handleElementMouseDown = (element, e) => {
    if (mode === "e" && e.button === 0) {
      setDraggedElement(element);
      setDraggedFromSidebar(true);
      e.preventDefault();
    }
  };

  const handleMouseUp = (e) => {
    if (draggedPlacedElementInfo) {
      const coords = getCanvasCoords(e);
      const bausteinHit = getBausteinAt(coords.x, coords.y);

      if (bausteinHit) {
        const details = getElementInsertionDetails(
          coords.x,
          coords.y,
          bausteinHit.baustein,
          draggedPlacedElementInfo.element.size,
        );
        addElementToBaustein(
          draggedPlacedElementInfo.element,
          bausteinHit.baustein.id,
          details.slot,
          details.compIndex,
        );
      } else {
        // Dropped in empty space. Return it to its original spot.
        const { bausteinId, elementIndex, element } = draggedPlacedElementInfo;
        setBausteinElements((prev) => {
          const newBausteinElements = { ...prev };
          const elements = [...(newBausteinElements[bausteinId] || [])];
          // Ensure we strip the flag when returning
          const { isMoving, ...cleanElement } = element;
          elements.splice(elementIndex, 0, cleanElement);
          newBausteinElements[bausteinId] = elements;
          return newBausteinElements;
        });
      }

      setDraggedPlacedElementInfo(null);
      setDraggedElementPosition(null);
    }
    setDropIndicator(null);
    setDraggedBaustein(null);
    setIsDragging(false);
  };

  const centerAndZoomView = (bausteine) => {
    const borderPadding = 150; // Increased to show dimension labels
    const groupMaxX = groupWidth * 250;
    const groupMaxY = groupHeight * 150;

    // Base the zoom and pan on the group area including the outer border
    let minX = -borderPadding;
    let maxX = groupMaxX + borderPadding;
    let minY = -borderPadding;
    let maxY = groupMaxY + borderPadding;

    // Expand if bausteine are outside group bounds (e.g. after resizing group smaller)
    if (bausteine.length > 0) {
      minX = Math.min(minX, ...bausteine.map((c) => c.x - borderPadding));
      maxX = Math.max(
        maxX,
        ...bausteine.map((c) => c.x + c.width + borderPadding),
      );
      minY = Math.min(minY, ...bausteine.map((c) => c.y - borderPadding));
      maxY = Math.max(
        maxY,
        ...bausteine.map((c) => c.y + c.height + borderPadding),
      );
    }

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const width = maxX - minX;
    const height = maxY - minY;

    const canvasPadding = 200;
    const zoomX = (canvasContainerWidth - canvasPadding) / width;
    const zoomY = (canvasContainerHeight - canvasPadding) / height;
    const newZoom = Math.min(zoomX, zoomY, 2);

    setZoom(newZoom);
    setPan({
      x: canvasContainerWidth / 2 - centerX * newZoom,
      y: canvasContainerHeight / 2 - centerY * newZoom,
    });
  };

  useEffect(() => {
    centerAndZoomView(placedBausteine);
  }, [groupWidth, groupHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoaded) {
      const config = {
        placedBausteine,
        bausteinElements,
        groupName,
        groupWidth,
        groupHeight,
        nextBausteinSerial,
        nextElementSerial,
      };
      localStorage.setItem("kf3_configuration", JSON.stringify(config));
    }
  }, [
    isLoaded,
    placedBausteine,
    bausteinElements,
    groupName,
    groupWidth,
    groupHeight,
    nextBausteinSerial,
    nextElementSerial,
  ]);

  const resetConfiguration = () => {
    setConfirmation({
      message:
        "Möchten Sie den Konfigurator wirklich zurücksetzen? Alle bisherigen Bausteine und Elemente gehen verloren.",
      confirmText: "Zurücksetzen",
      onConfirm: () => {
        setPlacedBausteine([]);
        setBausteinElements({});
        setGroupName("Verteilerschrank");
        setGroupWidth(2);
        setGroupHeight(2);
        setNextBausteinSerial(1);
        setNextElementSerial(1);
        localStorage.removeItem("kf3_configuration");
        triggerAlert("Konfigurator wurde zurückgesetzt.", "info");
      },
    });
  };

  const handleGroupResize = (newW, newH) => {
    // If expanding, just update
    if (newW >= groupWidth && newH >= groupHeight) {
      setGroupWidth(newW);
      setGroupHeight(newH);
      return;
    }

    // Checking for out-of-bounds bausteine
    const minX =
      placedBausteine.length > 0
        ? Math.min(...placedBausteine.map((c) => c.x))
        : 0;
    const minY =
      placedBausteine.length > 0
        ? Math.min(...placedBausteine.map((c) => c.y))
        : 0;
    const maxWidth = newW * 250;
    const maxHeight = newH * 150;

    let overflowingIndices = [];
    placedBausteine.forEach((cab, idx) => {
      if (
        cab.x + cab.width > minX + maxWidth ||
        cab.y + cab.height > minY + maxHeight
      ) {
        overflowingIndices.push(idx);
      }
    });

    if (overflowingIndices.length === 0) {
      setGroupWidth(newW);
      setGroupHeight(newH);
      return;
    }

    // Try to relocate overflowing bausteine
    let tempBausteine = [...placedBausteine];
    let success = true;

    for (let idx of overflowingIndices) {
      const cab = tempBausteine[idx];
      let found = false;
      const others = tempBausteine.filter((_, i) => i !== idx);

      // Attempt to find a new spot within new bounds
      for (let y = minY; y <= minY + maxHeight - cab.height; y += GRID_SIZE) {
        for (let x = minX; x <= minX + maxWidth - cab.width; x += GRID_SIZE) {
          const testCab = { ...cab, x, y };
          if (!checkCollision(testCab, -1, others)) {
            tempBausteine[idx] = testCab;
            found = true;
            break;
          }
        }
        if (found) break;
      }

      if (!found) {
        success = false;
        break;
      }
    }

    if (success) {
      setPlacedBausteine(tempBausteine);
      setGroupWidth(newW);
      setGroupHeight(newH);
    } else {
      setConfirmation({
        message:
          "Einige Bausteine passen nicht in die neue Größe. Trotzdem verkleinern?",
        confirmText: "Verkleinern",
        onConfirm: () => {
          setGroupWidth(newW);
          setGroupHeight(newH);
        },
      });
    }
  };

  const addBaustein = (bausteinType) => {
    const maxAllowedWidth = groupWidth * 250;
    const maxAllowedHeight = groupHeight * 150;

    if (
      bausteinType.width > maxAllowedWidth ||
      bausteinType.height > maxAllowedHeight
    ) {
      triggerAlert(
        `Baustein ist zu groß für die aktuelle Gruppengröße (${groupWidth}x${groupHeight} Einheiten).`,
        "error",
      );
      return;
    }

    const bausteinIndex = placedBausteine.length + 1;
    let foundX = null;
    let foundY = null;

    // Search for free space within the group area (top-to-bottom, left-to-right)
    // We scan using the GRID_SIZE to maintain alignment.
    for (
      let y = 0;
      y <= maxAllowedHeight - bausteinType.height;
      y += GRID_SIZE
    ) {
      for (
        let x = 0;
        x <= maxAllowedWidth - bausteinType.width;
        x += GRID_SIZE
      ) {
        const testBaustein = { ...bausteinType, x, y };
        if (!checkCollision(testBaustein)) {
          foundX = x;
          foundY = y;
          break;
        }
      }
      if (foundX !== null) break;
    }

    if (foundX === null) {
      triggerAlert("Kein freier Platz im Schrank verfügbar!", "error");
      return;
    }

    const serialId = `B${String(nextBausteinSerial).padStart(4, "0")}`;
    setNextBausteinSerial((prev) => prev + 1);

    const newBaustein = {
      ...bausteinType,
      catalogId: bausteinType.id,
      x: foundX,
      y: foundY,
      id: serialId,
      index: bausteinIndex,
    };

    const updatedBausteine = [...placedBausteine, newBaustein];
    setPlacedBausteine(updatedBausteine);
    setBausteinElements((prev) => ({ ...prev, [newBaustein.id]: [] }));
    setTimeout(() => centerAndZoomView(updatedBausteine), 0);
  };

  return (
    <div className={`app ${mode.toLowerCase()}-mode`}>
      <div className="custom-alert-container">
        {alerts.map((alert) => (
          <div key={alert.id} className={`custom-alert ${alert.type}`}>
            <div className="custom-alert-content">{alert.message}</div>
            <div
              className="custom-alert-close"
              onClick={() =>
                setAlerts((prev) => prev.filter((a) => a.id !== alert.id))
              }
            >
              &times;
            </div>
          </div>
        ))}

        {confirmation && (
          <div className="custom-alert confirm">
            <div className="custom-alert-content">
              {confirmation.message}
              <div className="confirm-buttons">
                <button
                  className="confirm-btn yes"
                  onClick={() => {
                    confirmation.onConfirm();
                    setConfirmation(null);
                  }}
                >
                  {confirmation.confirmText || "Löschen"}
                </button>
                <button
                  className="confirm-btn no"
                  onClick={() => setConfirmation(null)}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="kf-header">
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 200.93 126"
            id="svglogo"
            style={{ height: "40px", width: "auto" }}
          >
            <path d="M32.29,46.55h-14.77c-3.18,0-5.76-2.59-5.76-5.76,0-.03-.03-.06-.06-.06H.06s-.06,.03-.06,.06c0,9.66,7.85,17.51,17.51,17.51h14.77c9.66,0,17.51-7.86,17.51-17.51s-7.86-17.51-17.51-17.51h-14.77c-3.18,0-5.76-2.58-5.76-5.76s2.59-5.76,5.76-5.76h14.77c3.18,0,5.76,2.58,5.76,5.76,0,.04,.03,.06,.06,.06h11.63s.06-.03,.06-.06C49.8,7.86,41.94,0,32.29,0h-14.77C7.85,0,0,7.86,0,17.51s7.85,17.51,17.51,17.51h14.77c3.18,0,5.76,2.59,5.76,5.76s-2.59,5.76-5.76,5.76h0Z"></path>
            <path d="M57.21,58.3h11.63s.06-.03,.06-.06V.06s-.03-.06-.06-.06h-11.63s-.06,.03-.06,.06V58.23s.03,.06,.06,.06h0Z"></path>
            <path d="M107.6,19.02l-12.61,39.2s0,.04,0,.05c.01,.01,.03,.03,.05,.03h25.23s.04-.01,.05-.03c0-.02,.01-.04,0-.05l-12.61-39.2c-.02-.05-.1-.05-.12,0h0ZM101.6,.02s-.03-.02-.05-.02h-25.23s-.03,0-.05,.02c-.01,.02-.02,.04-.01,.05l12.61,39.2s.03,.04,.06,.04,.05-.01,.06-.04L101.62,.08s0-.04-.01-.05h0ZM139.05,.02s-.03-.02-.05-.02h-25.22s-.04,0-.05,.02c-.01,.02-.02,.04,0,.05l12.61,39.2s.03,.04,.06,.04c.03,0,.05-.01,.06-.04L139.05,.08s0-.04,0-.05h0ZM200.87,0h-11.63s-.06,.03-.06,.06V33.41c0,7.24-5.89,13.14-13.14,13.14s-13.14-5.9-13.14-13.14V.06s-.03-.06-.06-.06h-11.61s0,0,0,0c-.02,0-.03,0-.05,.02,0,0,0,0,0,0,0,0-.01,.01-.02,.02l-18.72,58.17s0,.04,0,.05c.01,.01,.03,.03,.05,.03h43.55c13.72,0,24.89-11.16,24.89-24.89V.06s-.03-.06-.06-.06h0Z"></path>
            <path d="M49.84,90.44c0-12.58-10.23-22.81-22.81-22.81H.06s-.06,.03-.06,.06v58.25s.03,.06,.06,.06H11.71s.06-.03,.06-.06v-12.7h15.26c.39,0,.77,0,1.15-.03,4.08-.2,8.02-1.5,11.41-3.75,6.41-4.23,10.24-11.34,10.24-19.03h0Zm-38.07-11.04h15.26c6.09,0,11.04,4.95,11.04,11.04,0,3.58-1.75,6.95-4.67,9.01-1.86,1.32-4.06,2.02-6.36,2.02H11.77v-22.07Z"></path>
            <path d="M56.44,126h49.72s.06-.03,.06-.06v-11.65s-.03-.06-.06-.06h-38v-46.54s-.03-.06-.06-.06h-11.65s-.06,.03-.06,.06v58.25s.03,.06,.06,.06h0Z"></path>
            <path d="M119.98,106.38h12.63s.04-.01,.05-.03c.01-.02,.02,.04,0-.05l-6.31-19.62c-.02-.05-.1-.05-.12,0l-6.31,19.62s0,.04,0,.05c.01,.01,.03,.03,.05,.03h0Zm16.38,11.53h-20.13s-.05,.02-.06,.04l-2.57,7.97s0,.04,0,.06c.01,.02,.03,.02,.05,.02h25.26s.04,0,.05-.02c0-.02,.01-.04,0-.06l-2.57-7.97s-.03-.04-.06-.04h0Zm64.51-50.27h-11.65s-.06,.03-.06,.06v39.21l-24.26-39.24s-.03-.03-.05-.03h-32.44s-.04,0-.05,.02c-.01,.02-.01,.04-.01,.05l18.75,58.25s0,0,0,0c.01,.02,.03,.04,.05,.04h11.65s.06-.03,.06-.06v-39.21l24.27,39.24s.03,.03,.05,.03h13.69s.06-.03,.06-.06v-58.25s-.03-.06-.06-.06h0Z"></path>
          </svg>

          <div>
            <h1
              style={{
                margin: 0,
                color: "#fbbf0b",
                fontSize: "2rem",
                letterSpacing: "-0.02em",
                textTransform: "uppercase",
                lineHeight: 1,
              }}
            >
              Verteilerschrank
            </h1>
            <p
              style={{
                margin: 0,
                opacity: 0.8,
                fontWeight: 500,
                fontSize: "0.9rem",
              }}
            >
              Konfigurator
            </p>
          </div>
        </div>

        <div className="mode-switcher">
          <button
            className={mode === "s" ? "active" : ""}
            onClick={() => setMode("s")}
            title="Schränke hinzufügen und anordnen"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="3" y1="9" x2="21" y2="9"></line>
              <line x1="9" y1="21" x2="9" y2="9"></line>
            </svg>
            Schränke
          </button>
          <button
            className={mode === "e" ? "active" : ""}
            onClick={() => setMode("e")}
            title="Elemente in Schränke einfügen"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12.89 2.162c-.368-.15-1.412-.15-1.78 0l-9 3.684A1 1 0 0 0 1.5 6.756v10.488a1 1 0 0 0 .61.922l9 3.684c.368.15 1.412.15 1.78 0l9-3.684a1 1 0 0 0 .61-.922V6.756a1 1 0 0 0-.61-.922z"></path>
              <polyline points="2.32 6.16 12 10.12 21.68 6.16"></polyline>
              <line x1="12" y1="22.76" x2="12" y2="10.12"></line>
            </svg>
            Elemente
          </button>
        </div>
      </div>
      <button
        className={`summary-toggle ${isSummaryOpen ? "open" : ""}`}
        onClick={() => setIsSummaryOpen(!isSummaryOpen)}
        title="Warenkorb anzeigen"
      >
        {isSummaryOpen ? (
          "✕"
        ) : (
          <>
            <span className="toggle-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            </span>
            {settings.showprices && (
              <span className="toggle-price">{totalPrice.toFixed(2)} €</span>
            )}
          </>
        )}
      </button>

      <div className={`summary-sidebar ${isSummaryOpen ? "open" : ""}`}>
        <div className="summary-header">
          <h3>Zusammenfassung</h3>
          {settings.showprices && (
            <div className="total-price-large">
              <span className="label">Gesamtbetrag</span>
              <span className="value">{totalPrice.toFixed(2)} €</span>
            </div>
          )}
        </div>

        <div className="summary-content">
          {placedBausteine.length === 0 &&
          Object.values(bausteinElements).flat().length === 0 ? (
            <div className="empty-summary">Keine Elemente ausgewählt</div>
          ) : (
            <>
              <div className="summary-column">
                {Object.keys(summaryData.bausteine).length > 0 && (
                  <div className="summary-section">
                    <h4>Bausteine</h4>
                    {Object.entries(summaryData.bausteine).map(([id, data]) => (
                      <div key={id} className="summary-row">
                        <span className="item-info">
                          <span
                            style={{
                              fontSize: "10px",
                              color: "#888",
                              marginRight: "5px",
                            }}
                          >
                            {data.id}
                          </span>
                          {data.name}
                        </span>
                        {settings.showprices && (
                          <span className="item-price">
                            {data.total.toFixed(2)} €
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {Object.keys(summaryData.elements).length > 0 && (
                  <div className="summary-section">
                    <h4>Elemente</h4>
                    {Object.entries(summaryData.elements).map(
                      ([uniqueId, data]) => (
                        <div key={uniqueId} className="summary-row">
                          <span
                            className="item-info"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <SidebarElementImage
                              element={data}
                              className="element-preview-summary"
                              height="24px"
                            />
                            <span>
                              <span
                                style={{
                                  fontSize: "10px",
                                  color: "#ff0000ff",
                                  marginRight: "5px",
                                }}
                              >
                                {data.id}
                              </span>
                              {data.name}
                            </span>
                          </span>
                          {settings.showprices && (
                            <span className="item-price">
                              {data.total.toFixed(2)} €
                            </span>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>

              <div className="summary-column">
                <div className="summary-section">
                  <h4>Kontakt für Bestellung</h4>
                  <div className="contact-form">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={contactForm.name}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            name: e.target.value,
                          })
                        }
                        placeholder="Ihr Name"
                      />
                    </div>
                    <div className="form-group">
                      <label>E-Mail</label>
                      <input
                        type="email"
                        className="form-input"
                        value={contactForm.email}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            email: e.target.value,
                          })
                        }
                        placeholder="ihre@email.de"
                      />
                    </div>
                    <div className="form-group">
                      <label>Telefonnummer</label>
                      <input
                        type="tel"
                        className="form-input"
                        value={contactForm.phone}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            phone: e.target.value,
                          })
                        }
                        placeholder="+49 123 456789"
                      />
                    </div>
                    <div className="form-group">
                      <label>Straße & Hausnummer</label>
                      <input
                        type="text"
                        className="form-input"
                        value={contactForm.street}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            street: e.target.value,
                          })
                        }
                        placeholder="Musterstraße 12"
                      />
                    </div>
                    <div className="form-row" style={{ display: "flex", gap: "10px" }}>
                      <div className="form-group" style={{ flex: "1" }}>
                        <label>PLZ</label>
                        <input
                          type="text"
                          className="form-input"
                          value={contactForm.zip}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              zip: e.target.value,
                            })
                          }
                          placeholder="12345"
                        />
                      </div>
                      <div className="form-group" style={{ flex: "2" }}>
                        <label>Stadt / Ort</label>
                        <input
                          type="text"
                          className="form-input"
                          value={contactForm.city}
                          onChange={(e) =>
                            setContactForm({
                              ...contactForm,
                              city: e.target.value,
                            })
                          }
                          placeholder="Musterstadt"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Bemerkungen</label>
                      <textarea
                        className="form-input"
                        value={contactForm.message}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            message: e.target.value,
                          })
                        }
                        placeholder="Ihre Nachricht oder Anmerkungen zur Bestellung"
                        rows="4"
                      ></textarea>
                    </div>
                    <button
                      className="checkout-btn"
                      style={{ marginTop: "10px", width: "100%" }}
                      onClick={handleSendRequest}
                    >
                      Anfrage senden
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="main-content">
        <div className="canvas-container">
          <canvas
            ref={canvasRef}
            width={canvasContainerWidth}
            height={canvasContainerHeight}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              cursor:
                draggedBaustein !== null || draggedPlacedElementInfo
                  ? "grabbing"
                  : isDragging
                    ? "grabbing"
                    : draggedElement
                      ? "copy"
                      : "grab",
            }}
          />

          <div className="group-settings">
            {/* <h4>Verteilerschrank</h4> */}
            <div className="setting-row">
              <label>Breite: </label>
              <div className="number-control">
                <button
                  onClick={() =>
                    handleGroupResize(Math.max(1, groupWidth - 1), groupHeight)
                  }
                >
                  -
                </button>
                <span>{groupWidth}</span>
                <button
                  onClick={() =>
                    handleGroupResize(Math.min(6, groupWidth + 1), groupHeight)
                  }
                >
                  +
                </button>
              </div>
            </div>
            <div className="setting-row">
              <label>Höhe: </label>
              <div className="number-control">
                <button
                  onClick={() =>
                    handleGroupResize(groupWidth, Math.max(1, groupHeight - 1))
                  }
                >
                  -
                </button>
                <span>{groupHeight}</span>
                <button
                  onClick={() =>
                    handleGroupResize(groupWidth, Math.min(12, groupHeight + 1))
                  }
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div className="controls">
            <div style={{ display: "flex" }}>
              <div>Zoom: {(zoom * 100).toFixed(0)}%</div>
            </div>

            <button onClick={() => centerAndZoomView(placedBausteine)}>
              Zentrieren
            </button>
          </div>

          <div className="config-buttons">
            <button
              className="config-btn remove"
              onClick={resetConfiguration}
              title="Konfigurator zurücksetzen"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
              Zurücksetzen
            </button>
          </div>
        </div>

        <div className="sidebar">
          {mode === "e" ? (
            <div className="category-filter">
              <label htmlFor="category-select">Kategorie & Suche</label>
              <select
                id="category-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {uniqueCategoriesFlattened.map((cat) => (
                  <option key={cat.path} value={cat.path}>
                    {"— ".repeat(cat.level) +
                      (cat.path === "all"
                        ? cat.name
                        : cat.name.charAt(0).toUpperCase() + cat.name.slice(1))}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="search-input"
                placeholder="Element suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          ) : (
            <div className="category-filter">
              <label htmlFor="baustein-category-select">
                Kategorie & Suche
              </label>
              <select
                id="baustein-category-select"
                value={selectedBausteinCategory}
                onChange={(e) => setSelectedBausteinCategory(e.target.value)}
              >
                {uniqueBausteinCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === "all"
                      ? "Alle Kategorien"
                      : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="search-input"
                placeholder="Baustein suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
          {mode === "s"
            ? bausteine
                .filter(
                  (b) =>
                    b.aktiv !== false &&
                    (selectedBausteinCategory === "all" ||
                      b.category === selectedBausteinCategory) &&
                    (b.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      b.name.toLowerCase().includes(searchTerm.toLowerCase())),
                )
                .map((baustein) => {
                  const isTooLarge =
                    baustein.width > groupWidth * 250 ||
                    baustein.height > groupHeight * 150;

                  return (
                    <div
                      key={baustein.id}
                      className={`baustein-item ${isTooLarge ? "too-large" : ""}`}
                      onClick={() =>
                        mode === "s" && !isTooLarge && addBaustein(baustein)
                      }
                      title={
                        isTooLarge
                          ? "Baustein ist zu groß für die aktuelle Gruppengröße"
                          : ""
                      }
                    >
                      <div className="baustein-id">{baustein.id}</div>
                      <div className="baustein-name">{baustein.name}</div>
                      {/* <div className="baustein-size">
                      {baustein.width}×{baustein.height}mm ({gridUnits} Einheiten)
                      {isTooLarge && <span className="error-text"> (Zu groß)</span>}
                    </div> */}
                      {settings.showprices && (
                        <div className="baustein-price">
                          Preis: {baustein.price} €
                        </div>
                      )}
                    </div>
                  );
                })
            : elements
                .filter((el) => {
                  if (el.aktiv === false) return false;

                  const matchesSearch =
                    el.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    el.name.toLowerCase().includes(searchTerm.toLowerCase());
                  if (!matchesSearch) return false;

                  if (selectedCategory === "all") return true;

                  const elPath = el.subcategory
                    ? `${el.category} > ${el.subcategory}`
                    : el.category;

                  return (
                    elPath === selectedCategory ||
                    elPath.startsWith(selectedCategory + " > ")
                  );
                })
                .map((element) => (
                  <div
                    key={element.id}
                    className="baustein-item element-item-sidebar"
                    onMouseDown={(e) => handleElementMouseDown(element, e)}
                    onClick={(e) => {
                      if (mode === "e" && !draggedElement) {
                        e.preventDefault();
                        addElementToBaustein(element);
                      }
                    }}
                    style={{
                      cursor:
                        mode === "e"
                          ? draggedElement === element
                            ? "grabbing"
                            : "grab"
                          : "pointer",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        alignItems: "center",
                      }}
                    >
                      <SidebarElementImage
                        element={element}
                        className="element-preview-sidebar"
                        height="40px"
                      />
                      <div style={{ flex: 1 }}>
                        <div className="baustein-id">{element.id}</div>
                        <div className="baustein-name">{element.name}</div>
                        <div className="baustein-size">
                          Größe {element.size} - {element.type}
                        </div>
                        {settings.showprices && (
                          <div className="baustein-price">
                            Preis: {element.price} €
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#666",
                        marginTop: "4px",
                        textAlign: "right",
                      }}
                    >
                      {selectedBaustein
                        ? `→ Baustein ${selectedBaustein}`
                        : "Baustein auswählen"}
                    </div>
                  </div>
                ))}
        </div>
      </div>
    </div>
  );
}

export default App;
